/**
 * Unified campaign create/edit form (task 004-05).
 *
 * Rewritten to align with admin-requirements.md §6 (Jul-29 review call + Jul-24 conventions).
 *
 * Key changes from epic-002:
 * - Campaign-style picker replaces bare flowKind (2 delivery-surface styles: Co-op, Custom Opt-in)
 * - Dynamic per-style form sections (design note table)
 * - Direct CPC URL scoped to Custom Opt-in, click-out send mode only
 * - Form section order: Pricing & limits → Restrictions → Suppression
 * - Plain-language labels for journey-model sections
 * - Generated page link as first-class fact (slug + copy-full-URL)
 * - Partner Send reworked as one transport config with explicit method
 * - Thank-you message defaults to production copy
 * - Publisher selector removed from form
 * - Sub-type picker removed
 * - Creative section gated on Paid Social (presentsOffer flag; Q66 answered 2026-08-11)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, CampaignDetailResponse } from '../lib/api';
import {
  AdvertiserResponse,
  CampaignAggregateCreateInput,
  CampaignAggregateUpdateInput,
  CampaignResponse,
  CampaignStatus,
  CampaignStatusReason,
  CrmResponseRules,
  INLINE_TRANSPORT_KEYS,
  isWithinRunWindow,
  normalizeCrmConfig,
  PartnerCredentialRowResponse,
  PartnerEndpointResponse,
  resolveCapiEnabled,
  SuppressionFileResponse,
  campaignResponseSchema,
  FACEBOOK_SITE_ID,
  facebookShareLimit,
  OPT_IN_DISCLAIMER_DEFAULT,
  type TestRunDetail,
} from '@leads-platform/shared';
// Partner endpoints are read through the endpoint screen's own fetch module —
// imported, never edited (ADR-010 A2 (b): `lib/api.ts` stays closed to Stage-C
// screens, and `screens/partner-endpoints/**` is a closed epic's Owns).
import { getPartnerEndpoints } from './partner-endpoints/api';
import { getPartnerCredentials } from './partner-credentials/api';
// Partner-config check (085 D9): the server's dry run, surfaced before Save.
import { checkCampaignPartnerConfig } from './partner-config-check/api';
import { PartnerConfigIssues } from './partner-config-check/partner-config-issues';
import { SourceMixPanel } from './facebook-share/source-mix-panel';
import {
  hasNewBlocker,
  usePartnerConfigCheck,
} from './partner-config-check/use-partner-config-check';
// Test-run panel (034-04): imported as-is per the task file's contract — no
// edits to screens/test-runs/** or lib/api.ts.
import { AdminRunPanel } from './test-runs/admin-run-panel';
import { RunDetail } from './test-runs/run-detail';
import { getRun } from './test-runs/test-runs.api';
import { useRunPolling } from './test-runs/use-run-polling';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/ui/page-header';
import { FormSection, Field, FieldBlock } from '../components/ui/form-section';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { StatusPill } from '../components/ui/status-pill';
import { Textarea } from '../components/ui/textarea';
import { Banner } from '../components/ui/banner';
import { Toggle } from '../components/ui/toggle';
import { FileDropZone } from '../components/file-drop-zone';
import { CampaignImageInput } from '../components/campaign-image-input';
import { MainContentEditor } from '../components/main-content-editor';
import { PageLinkField, Shortcodes, RadioGroup, SiteIdPicker } from '../components/formbits';
import { CopyButton } from '../components/ui/copy-button';
import {
  CtoLinksSection,
  draftFieldErrors,
  payloadFor,
  type LinkDraft,
} from '../components/cto-links-section';
// ESP destinations section (epic 101 fix 06, D7): the catalog + section JSX
// live in their own component — one home for brand-list data, rendered under
// this form's flags.hasEspDestinations gate. BEEHIIV_PUB_IDS is re-exported
// below (campaign-form.test.tsx:19 is its historical import site).
import {
  ESP_DEFAULT_DESTINATIONS,
  EspDestinationsSection,
  type EspDestinationRow,
} from '../components/esp-destinations-section';
import { Typeahead } from '../components/typeahead';
import { DateRangeField } from '../components/date-range-field';
import { useToast } from '../components/ui/toast';
import { DEFAULT_PAGE_HTML } from '../lib/campaign-template-default';
import {
  CAMPAIGN_STYLES as CAMPAIGN_STYLE_LIST,
  THANK_YOU_DEFAULT,
  CRM_PLACEHOLDERS,
  PLACEHOLDER_SYNTAXES,
  STYLE_LABEL_TO_ENUM,
  OFFER_PAGE_BASE,
  extFormPostUrl,
  EXT_FORM_HIDDEN_FIELDS,
  campaignStyleEnumToLabel,
  isJourneyStyle,
  isCoopStyle,
  isCustomOptinStyle,
  isPaidSocialStyle,
  isCtoEmailStyle,
  isExternalFormStyle,
  styleToFlowKind,
  type CampaignStyle,
  type CampaignStyleEnumValue,
} from '../lib/campaign-styles';
import clsx from 'clsx';

// ---------------------------------------------------------------------------
// Static option lists
// ---------------------------------------------------------------------------

// Status is lifecycle-driven only: a create defaults to 'pending' — or creates
// directly as 'scheduled' via the create-mode Save & schedule action (069-03
// D4) — and afterwards transitions via the start/schedule/pause + complete
// buttons in edit mode. There is no manual status picker.

const LIMIT_TYPE_OPTIONS = [
  { value: 'total_clicks', label: 'Total Clicks Limit' },
  { value: 'unique_leads', label: 'Unique Leads Limit' },
  { value: 'unique_clicks', label: 'Unique Clicks Limit' },
] as const;

// LIMIT_TYPE_OPTIONS must mirror limitTypeEnum in packages/shared/src/domain/enums.ts.
// Backend enum: {total_clicks, unique_leads, unique_clicks}.
// total_leads is NOT offered per admin-requirements.md §3.1.

// HF-124: a new form opens as Co-op, whose effect forces unique_leads, and that value leaked into
// Custom Opt-in click-out pages; a send-mode transition re-derives the default (other values stay).
function limitTypeAfterSendMode(limitType: string, nextMode: string): string {
  if (nextMode === 'click_out' && (limitType === '' || limitType === 'unique_leads')) {
    return 'total_clicks';
  }
  if (nextMode === 'lead_post' && limitType === 'total_clicks') {
    return 'unique_leads';
  }
  return limitType;
}

// HF-110: present-tense section copy. Strings render on three kinds of form: a campaign
// that posts its own leads to a partner (Co-op, or a lead-post Custom Opt-in), a click-out
// Custom Opt-in, and a Paid Social campaign. Which one applies is `copyMode` in the form.
const HF110_COPY = {
  send: {
    pricing:
      "Caps and limits apply when a lead is sent to this campaign's partner: the daily cap stops sends until midnight ET, and reaching the Limit completes the campaign. Total Revenue is not here — it is a reconciliation output in Stats & export.",
    limitType:
      "Unique Leads (or none) counts the leads sent to the partner; Total Clicks and Unique Clicks count the page's Accept clicks. Reaching the Limit hides the page and completes the campaign.",
    capNote:
      'at the daily cap, sends stop until midnight ET and the status doesn\'t change. At the Limit, the campaign is completed and the list shows "Completed — cap hit"; raise the Limit before reopening it.',
    restrictions:
      "Checked before each lead is sent to this campaign's partner: a lead from a restricted site ID or a blocked email domain is skipped for this campaign.",
    suppression:
      "Per-campaign MD5 suppression file and/or pre-ping API. SHA256 is cut from R1. Both are checked before each send to this campaign's partner, and a Custom Opt-in page is also hidden on the offers walk from a suppressed email or one the pre-ping turns down.",
    siteIdsHelper: 'Leads from these site IDs are skipped for this campaign.',
  },
  clickOut: {
    pricing:
      'This campaign is click-out: the visitor goes to the Direct CPC URL and no lead is posted to a partner, so the Daily cap is stored, not enforced, and the Limit counts Accept clicks. Total Revenue is not here — it is a reconciliation output in Stats & export.',
    limitType:
      'Total Clicks counts every Accept click on this page; Unique Clicks counts one per visitor IP. At the Limit the page leaves the walk and the campaign is completed.',
    capNote:
      'a click-out campaign posts no leads to a partner, so its daily cap is never reached. At the Limit, the page leaves the walk, the campaign is completed and the list shows "Completed — cap hit"; raise the Limit before reopening it.',
    restrictions:
      'Restricted site IDs and blocked email domains are stored, not checked, on a click-out campaign: no lead is posted to a partner.',
    suppression:
      'Per-campaign MD5 suppression file and/or pre-ping API. SHA256 is cut from R1. A click-out campaign posts no leads, so only the offers walk checks them: its page is hidden from a suppressed email or one the pre-ping turns down.',
    siteIdsHelper: 'Stored, not checked, on a click-out campaign.',
  },
  paidSocial: {
    pricing:
      "A Paid Social lead is sent to Co-op partners, and their caps and limits apply; this campaign's daily cap and Limit are stored, not enforced. Total Revenue is not here — it is a reconciliation output in Stats & export.",
    limitType: 'Stored, not enforced on a Paid Social campaign.',
    capNote:
      "a Paid Social campaign's own caps are not enforced; each Co-op campaign that receives its leads applies its own.",
    restrictions:
      'Restricted site IDs and blocked email domains are stored, not checked, on a Paid Social campaign: its leads go to Co-op partners, whose own restrictions apply.',
    suppression:
      'Per-campaign MD5 suppression file and/or pre-ping API. SHA256 is cut from R1. Stored, not checked, on a Paid Social campaign: its leads go to Co-op partners, whose own suppression and pre-ping apply.',
    siteIdsHelper: 'Stored, not checked, on a Paid Social campaign.',
  },
} as const;

// The ESP destinations catalog (BRAND_LIST_OPTIONS,
// LEGACY_BRAND_LIST_OPTIONS, BEEHIIV_PUB_IDS) and the D4 journey seed
// (ESP_DEFAULT_DESTINATIONS) moved to components/esp-destinations-section.tsx
// in epic 101 fix 06 (D7) — the section's one shared home, so a connector-list
// change lands once. BEEHIIV_PUB_IDS stays importable from this module:
// campaign-form.test.tsx is its historical import site (CAMPAIGN_STYLES
// re-export precedent).
export { BEEHIIV_PUB_IDS } from '../components/esp-destinations-section';

// 067-06 (README D6): the Maropost TT row's journey trigger + tags ride the
// row's free-form fieldMappings (no migration, no shared change) as
// maropostJourney { journeyId, triggerUid } and maropostTags string[] — the
// api's parseMaropostDestinationConfig reads them; every other key round-trips
// untouched. HF-084 (Manny, Q124): the journey is ruled out — the inputs and
// the "no trigger" banner are hidden, and a new TT row is no longer prefilled.
// A row already holding these keys still round-trips them unchanged on save.
function isMaropostTTRow(row: { provider: string; brandList: string }): boolean {
  return row.provider === 'maropost' && row.brandList === 'tt';
}

/** The TT sub-row inputs as stored — '' for anything unset or not a string. */
function maropostInputsFrom(
  fieldMappings: Record<string, unknown> | null | undefined
): Required<Pick<EspDestinationRow, 'maropostJourneyId' | 'maropostTriggerUid' | 'maropostTags'>> {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const journey: unknown = fieldMappings?.maropostJourney;
  const tags: unknown = fieldMappings?.maropostTags;
  const isJourneyObject = typeof journey === 'object' && journey !== null;
  return {
    maropostJourneyId: isJourneyObject && 'journeyId' in journey ? text(journey.journeyId) : '',
    maropostTriggerUid: isJourneyObject && 'triggerUid' in journey ? text(journey.triggerUid) : '',
    maropostTags: Array.isArray(tags)
      ? tags.filter((tag): tag is string => typeof tag === 'string').join(', ')
      : '',
  };
}

/** The comma-separated Tags input as stored: trimmed, blanks dropped, de-duplicated. */
function parseMaropostTags(text: string): string[] {
  return [
    ...new Set(
      text
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag !== '')
    ),
  ];
}

/**
 * The TT row's fieldMappings on save: the stored keys, with the two owned
 * ones replaced by the inputs — maropostJourney whenever either id is typed
 * (so a prefilled 11 survives a save made before the UID is known),
 * maropostTags whenever a tag is typed; each dropped when empty. Other rows
 * pass through unchanged.
 */
function withMaropostTTConfig(
  fieldMappings: Record<string, unknown>,
  row: EspDestinationRow
): Record<string, unknown> {
  if (!isMaropostTTRow(row)) return fieldMappings;
  const next = { ...fieldMappings };
  delete next.maropostJourney;
  delete next.maropostTags;
  const journeyId = row.maropostJourneyId?.trim() ?? '';
  const triggerUid = row.maropostTriggerUid?.trim() ?? '';
  if (journeyId !== '' || triggerUid !== '') {
    next.maropostJourney = { journeyId, triggerUid };
  }
  const tags = parseMaropostTags(row.maropostTags ?? '');
  if (tags.length > 0) {
    next.maropostTags = tags;
  }
  return next;
}

// Must mirror eligibilityStepKindEnum in packages/shared/src/domain/enums.ts —
// EligibilityStepKind below feeds the wire payload's stepKind, so a drift fails
// the typecheck at the payload build in aggregateFromForm.
const ELIGIBILITY_STEP_KIND_OPTIONS = [
  {
    value: 'cloudflare',
    label: 'Cloudflare Turnstile',
    description: 'Human verification — per-campaign configurable, only on certain inbound sources',
  },
  {
    value: 'email_oversight',
    label: 'EmailOversight',
    description: 'Email address validation via the EmailOversight API',
  },
  {
    value: 'internal_duplicate',
    label: 'Internal Duplicate Check',
    description: 'A duplicate is not automatically ineligible — configured behavior',
  },
] as const;

type EligibilityStepKind = (typeof ELIGIBILITY_STEP_KIND_OPTIONS)[number]['value'];

const BEHAVIOR_ON_FAIL_OPTIONS = [
  { value: 'halt', label: 'Halt' },
  { value: 'continue', label: 'Continue' },
  { value: 'convert_to_indeterminate', label: 'Convert to Indeterminate' },
];

const BEHAVIOR_ON_ERROR_OPTIONS = [
  { value: 'halt', label: 'Halt' },
  { value: 'continue', label: 'Continue' },
  { value: 'convert_to_indeterminate', label: 'Convert to Indeterminate' },
];

const BEHAVIOR_ON_INDETERMINATE_OPTIONS = [
  { value: 'halt', label: 'Halt' },
  { value: 'continue', label: 'Continue' },
];

// B2 operator decision: onDuplicate collapsed to 'continue' | 'fail'
const ON_DUPLICATE_OPTIONS = [
  { value: 'continue', label: 'Allow (soft signal)' },
  { value: 'fail', label: 'Block (hard reject)' },
];

const HASH_ALGORITHM_OPTIONS = [{ value: 'md5', label: 'MD5' }];

// ---------------------------------------------------------------------------
// Campaign style constants
// The picker source now lives in lib/campaign-styles (CAMPAIGN_STYLES gained
// 'External Form' — epic 096 task 03); this re-export is the form's historical
// import site.
// ---------------------------------------------------------------------------

const CAMPAIGN_STYLES = CAMPAIGN_STYLE_LIST;

// Derived visibility flags per style matrix (README #4).
// Journey sections (Capture strategy, Consent, Eligibility)
// render for paid_social, cto_email and — epic 096 D1 — external_form (the
// third acquisition journey; consent + ESP destinations apply to it).
function useStyleFlags(style: CampaignStyle) {
  const isCoop = isCoopStyle(style);
  const isOptin = isCustomOptinStyle(style);
  const isPaidSocial = isPaidSocialStyle(style);
  const isCtoEmail = isCtoEmailStyle(style);
  const isExternalForm = isExternalFormStyle(style); // epic 096 D1/task 03
  const isJourney = isJourneyStyle(style); // true for Paid Social, CTO Email, External Form
  // Epic 101 fix 06 (D7 amends D2/D3): ESP destinations are a property of the
  // three journey styles only — Co-op placement was an error in the original
  // request. Co-op renders no section, seeds nothing (D4) and its update
  // payload omits the key entirely (see submit), so stored rows persist
  // untouched (R2).
  const hasEspDestinations = isJourney;
  const hasPartnerSend = isCoop || isOptin; // Partner Send renders for Co-op + Custom Opt-in
  // Hosted page slug for all non-Co-op EXCEPT external_form (D4: slug-less —
  // the hasHostedPage payload block still round-trips stored values for the
  // four legacy styles; external_form never had a page).
  const hasHostedPage = !isCoop && !isExternalForm;
  // The Offer page section (page title, thank-you, Templates, Main Content, CTA)
  // is hidden for CTO Email — "nothing typed" (admin-requirements.md §2.9, 045-03)
  // and for External Form (D4: no editor — the form lives on the partner site).
  // hasHostedPage still gates the payload block so stored values round-trip.
  const hasOfferPage = hasHostedPage && !isCtoEmail;
  const presentsOffer = isPaidSocial; // Paid Social presents an offer: gates the bannerImageUrl payload. Q66 answered 2026-08-05; Creative fields (Headline + Offer body) removed per FR-A28 v0.18 (HF-079) — the Main Content editor carries the heading; CTO Email has no consumer since 045-02; Custom Opt-in is Q106 (epic 050-01); the Follow-on components section it used to also gate was removed as dead UI (HF-096)

  return {
    isCoop,
    isOptin,
    isPaidSocial,
    isCtoEmail,
    isExternalForm,
    isJourney,
    hasEspDestinations,
    hasPartnerSend,
    hasHostedPage,
    hasOfferPage,
    presentsOffer,
  };
}

// ---------------------------------------------------------------------------
// Form state shape
// ---------------------------------------------------------------------------

interface EligibilityStepRow {
  id: string;
  stepKind: EligibilityStepKind;
  configJson: string;
}

interface FormState {
  // Basics
  name: string;
  style: CampaignStyle;
  campaignStyle: CampaignStyleEnumValue;
  advertiserId: string;
  clientName: string;
  status: string;
  // ADR-010 §3 / A16 (054-05): why the campaign left its running state —
  // display only. Optional so existing FormState builders (campaign-form.
  // preview.test.tsx) compile without change, the transactionalCampaignId?
  // precedent above; EMPTY_FORM defaults it to null, formFromCampaign hydrates
  // it from the response, and no payload ever carries it (the write schema
  // omits the key; the server owns every transition).
  statusReason?: CampaignStatusReason | null;
  usOnly: boolean;
  // Money & limits
  pricePerLead: string;
  // 066 D4: the Co-op routing pin ('' = no pin). Optional for the same reason
  // `statusReason` is — campaign-form.preview.test.tsx builds FormState literals
  // and is out of this task's scope. EMPTY_FORM and the edit load always set it,
  // so the running form never sees it undefined.
  priority?: string;
  limitType: string;
  limitValue: string;
  dailyCap: string;
  // Epic 091 D9: the Co-op "Max Facebook share of daily cap" ('' = no limit).
  // Optional for the same reason `priority` is — campaign-form.preview.test.tsx
  // builds FormState literals and must keep compiling. EMPTY_FORM and the edit
  // load always set it, so the running form never sees it undefined.
  maxFacebookSharePct?: string;
  // Schedule
  startDate: string;
  endDate: string;
  // Restrictions
  domainRestrictions: string;
  restrictedSiteIds: string[];
  // Suppression
  suppressionEnabled: boolean;
  suppressionPrepingUrl: string;
  suppressionFileId: string;
  suppressionPendingFile: File | null;
  // #49: server-derived — the linked file is unreadable, so suppression is being
  // skipped for this campaign. Set on the edit load only (detail-read flag).
  suppressionFileUnreadable: boolean;
  // Offer page (Custom Opt-in)
  pageSlug: string;
  pageTitle: string;
  optInDisclaimer: string; // HTML; empty = resolved advertiser line on custom_optin, else the built-in TradingTips default (FR-A37 / 026-02 / 054-03)
  ctaText: string; // Offer CTA label (epic 042); empty = platform default "Get My Free Report"
  // Offer-page template discriminator (epic 106): '' = Standard (NULL on the
  // wire), 'partner-hero-v1' = the Partner hero look. Optional for the same
  // reason the 034-05/091 fields are — campaign-form.preview.test.tsx builds
  // FormState literals and is out of this epic's scope. EMPTY_FORM and the
  // edit load always set it, so the running form never sees it undefined.
  offerTemplate?: '' | 'partner-hero-v1';
  headline: string; // Partner-hero H1 (epic 106); empty = the layout's headline fallback
  introHtml: string; // Partner-hero Intro Copy (epic 106), BlockNote HTML; empty = no intro block
  htmlContent: string;
  thankYouMessage: string;
  useAsTemplate: boolean;
  gaEventLabel: string; // GA click label for hosted-page styles (FR-A32 / 019-04)
  // A14 (034-05): Meta CAPI three-state control. Optional so existing FormState
  // builders (campaign-form.preview.test.tsx) compile without change; EMPTY_FORM
  // always sets it so the running form never sees it undefined.
  capiEnabled?: 'default' | 'on' | 'off';
  // Journey primitives
  captureStrategy: string;
  consentBasis: string;
  consentEvidence: string;
  eligibilitySteps: EligibilityStepRow[];
  espDestinations: EspDestinationRow[];
  // Partner Send
  sendMode: string; // 'lead_post' | 'click_out' (mode toggle)
  sendMethod: string; // GET | POST (transport method)
  sendSyntax: string;
  sendUrl: string;
  directCpcUrl: string; // Custom Opt-in only
  // Partner Send transport mode (034-02, ADR-016 §2). DERIVED on load from the
  // presence of crm_config.endpoint_id and never stored under a key of its own:
  // 'endpoint' means the advertiser's partner endpoint supplies the transport,
  // 'simple' means the inline URL fields do. Switching the radio leaves the
  // other mode's fields in state — only the save applies the payload rule.
  // Optional for the same reason `crmConfigRest` is (HF-019): this type is
  // module-local and a second builder of it lives in `campaign-form.preview.
  // test.tsx`, which this epic does not own. EMPTY_FORM always sets all three,
  // so the running form never sees them undefined.
  sendVia?: 'simple' | 'endpoint';
  endpointId?: string; // crm_config.endpoint_id, string form (sent as a number)
  endpointValues?: Record<string, string>; // crm_config.values, keyed by variable.key
  crmConfigRest?: Record<string, unknown>; // unrendered crmConfig keys kept verbatim (HF-019)
  // 034-03: auth + response_rules rendered as discrete form fields. Optional for
  // the same reason sendVia/crmConfigRest are (HF-019): campaign-form.preview.
  // test.tsx builds FormState objects directly and is out of this epic's Owns.
  // EMPTY_FORM always sets all five, so the running form never sees them undefined.
  credentialId?: string; // auth.credential_id, string form
  authRest?: Record<string, unknown>; // auth minus credential_id (non-rendered auth props kept)
  acceptedContains?: string; // response_rules.accepted_contains, joined ', '
  duplicateContains?: string; // response_rules.duplicate_contains, joined ', '
  rejectedContains?: string; // response_rules.rejected_contains, joined ', '
  // Placement (HLP)
  placementSequence: string;
  placementSpot: string;
  placementIsThankYouAnchor: boolean;
  // The placement the server held when this form state was built (edit load,
  // post-save refresh, lifecycle refresh); absent when the campaign had none.
  // Epic 083 D6: it feeds the read-only placement line — the form never sends
  // a placement. Optional for the same reason the 034-03 fields are:
  // campaign-form.preview.test.tsx builds FormState objects directly.
  loadedPlacement?: { sequence: number | null; spot: number | null; isThankYouAnchor: boolean };
  // ClickMagick
  cmEnabled: boolean;
  cmDomain: string;
  cmName: string;
  cmSlug: string;
  // Creative (banner image feeds the welcome page's OG image; the Headline +
  // Offer body fields are removed — FR-A28 v0.18, HF-079)
  bannerImageUrl: string;
  // `/ext` interstitial settings (epic 096 D4/D5a, task 03) — rendered for
  // External Form only; '' means "not set" (saved as null). Optional for the
  // same reason maxFacebookSharePct is: campaign-form.preview.test.tsx builds
  // FormState literals and is out of this task's scope.
  errorUrl?: string;
  siteId?: string; // the site business key ('fsa'), '' = none
  // FB campaign mapping key (epic 109 task 01): the Meta campaign record ID
  // whose spend maps to this campaign. Rendered in the External Form section
  // beside Site, but NOT style-gated on the wire (ruling 1 — any style may
  // carry it). '' = not set (sent as null). Optional like its /ext siblings:
  // campaign-form.preview.test.tsx builds FormState literals.
  fbCampaignId?: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  style: 'Co-op',
  campaignStyle: 'co_op',
  advertiserId: '',
  clientName: '',
  status: 'pending',
  statusReason: null,
  usOnly: false,
  pricePerLead: '',
  priority: '',
  limitType: '',
  limitValue: '',
  dailyCap: '',
  maxFacebookSharePct: '',
  startDate: '',
  endDate: '',
  domainRestrictions: '',
  restrictedSiteIds: [],
  suppressionEnabled: false,
  suppressionPrepingUrl: '',
  suppressionFileId: '',
  suppressionPendingFile: null,
  suppressionFileUnreadable: false,
  pageSlug: '',
  pageTitle: '',
  // FR-A37 (026-02): the built-in TradingTips disclaimer. Kept as the mounted
  // create-form default for every style; the style-aware prefill (054-03)
  // swaps it for '' while the style is Custom Opt-in. Clearing the field
  // stores NULL → the landing fallback renders.
  optInDisclaimer: OPT_IN_DISCLAIMER_DEFAULT,
  ctaText: '',
  offerTemplate: '',
  headline: '',
  introHtml: '',
  htmlContent: '',
  thankYouMessage: THANK_YOU_DEFAULT,
  useAsTemplate: false,
  gaEventLabel: '',
  capiEnabled: 'default',
  captureStrategy: '', // No default — server owns validity, hidden sections never contribute payload
  consentBasis: 'on_page',
  consentEvidence: '',
  eligibilitySteps: [],
  espDestinations: [],
  sendMode: 'lead_post',
  sendMethod: 'GET',
  sendSyntax: 'percent',
  sendUrl: '',
  directCpcUrl: '',
  sendVia: 'simple',
  endpointId: '',
  endpointValues: {},
  crmConfigRest: {},
  credentialId: '',
  authRest: {},
  acceptedContains: '',
  duplicateContains: '',
  rejectedContains: '',
  placementSequence: '',
  placementSpot: '',
  placementIsThankYouAnchor: false,
  cmEnabled: false,
  cmDomain: '',
  cmName: '',
  cmSlug: '',
  bannerImageUrl: '',
  // epic 096 task 03: empty = not set (saved as null on the wire)
  errorUrl: '',
  siteId: '',
  // epic 109 task 01: empty = not set (saved as null on the wire)
  fbCampaignId: '',
};

let rowCounter = 0;
const newRowId = () => `row-${++rowCounter}`;

// ---------------------------------------------------------------------------
// Eligibility step config helpers
// ---------------------------------------------------------------------------

/**
 * Creation/add-time default configs for the two operator-mandated steps
 * (epic 022 task 02, operator decision 2026-09-01). Written as EXPLICIT data —
 * the engine's absent-key default for onError is 'halt'
 * (packages/api/src/modules/capture/eligibility/types.ts StepBehaviorConfig),
 * which contradicts the requested "check cannot decide or errors → Continue",
 * so the policies must be present in the stored config, not implied.
 * parseEligibilityConfig fallbacks and engine defaults stay untouched: these
 * values apply only when a step row is created (create-mode seeding and the
 * manual Add buttons), never when an existing campaign is loaded.
 */
const ELIGIBILITY_STEP_DEFAULT_CONFIG: Record<'cloudflare' | 'email_oversight', string> = {
  cloudflare: JSON.stringify({ onFail: 'halt', onError: 'continue' }),
  email_oversight: JSON.stringify({ onFail: 'halt', onIndeterminate: 'continue' }),
};

/**
 * Parse eligibility step configJson with safe defaults.
 * Absent keys yield engine defaults, never invented values.
 * B1: removed `required` key (plugin no longer reads it).
 * B2: `onDuplicate` collapsed to 'continue' | 'fail'.
 */
function parseEligibilityConfig(configJson: string): {
  onFail: 'halt' | 'continue' | 'convert_to_indeterminate';
  onError: 'halt' | 'continue' | 'convert_to_indeterminate';
  onIndeterminate: 'halt' | 'continue';
  onDuplicate?: 'continue' | 'fail';
} {
  try {
    const parsed = JSON.parse(configJson);
    return {
      onFail: parsed.onFail ?? 'halt',
      onError: parsed.onError ?? 'halt',
      onIndeterminate: parsed.onIndeterminate ?? 'continue',
      onDuplicate: parsed.onDuplicate,
    };
  } catch {
    return {
      onFail: 'halt',
      onError: 'halt',
      onIndeterminate: 'continue',
    };
  }
}

/**
 * Update eligibility step configJson by merging partial changes.
 * B3: Per-kind serialization - only keys relevant to the step kind are emitted.
 * Absent keys rely on engine defaults.
 * B1: removed `required` key (plugin no longer reads it).
 * B2: `onDuplicate` collapsed to 'continue' | 'fail'.
 */
function updateEligibilityConfig(
  configJson: string,
  changes: Partial<{
    onFail: 'halt' | 'continue' | 'convert_to_indeterminate';
    onError: 'halt' | 'continue' | 'convert_to_indeterminate';
    onIndeterminate: 'halt' | 'continue';
    onDuplicate: 'continue' | 'fail';
  }>,
  stepKind?: string
): string {
  const current = parseEligibilityConfig(configJson);
  const merged = { ...current };

  if (changes.onFail !== undefined) merged.onFail = changes.onFail;
  if (changes.onError !== undefined) merged.onError = changes.onError;
  if (changes.onIndeterminate !== undefined) merged.onIndeterminate = changes.onIndeterminate;
  if (changes.onDuplicate !== undefined) merged.onDuplicate = changes.onDuplicate;

  // B3: Serialize only keys relevant to the step kind
  const toSerialize: Record<string, unknown> = {};

  if (stepKind === 'cloudflare') {
    // cloudflare: onFail + onError only
    if (merged.onFail !== undefined) toSerialize.onFail = merged.onFail;
    if (merged.onError !== undefined) toSerialize.onError = merged.onError;
  } else if (stepKind === 'email_oversight') {
    // email_oversight: onFail + onIndeterminate only
    if (merged.onFail !== undefined) toSerialize.onFail = merged.onFail;
    if (merged.onIndeterminate !== undefined) toSerialize.onIndeterminate = merged.onIndeterminate;
  } else if (stepKind === 'internal_duplicate') {
    // internal_duplicate: onDuplicate + onError only
    if (merged.onDuplicate !== undefined) toSerialize.onDuplicate = merged.onDuplicate;
    if (merged.onError !== undefined) toSerialize.onError = merged.onError;
  } else {
    // Unknown/fallback: serialize all keys
    toSerialize.onFail = merged.onFail;
    toSerialize.onError = merged.onError;
    toSerialize.onIndeterminate = merged.onIndeterminate;
    if (merged.onDuplicate !== undefined) toSerialize.onDuplicate = merged.onDuplicate;
  }

  return JSON.stringify(toSerialize);
}

// ---------------------------------------------------------------------------
// Date conversion helpers
// ---------------------------------------------------------------------------

const ISO_TO_DATE = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

const DATE_TO_ISO = (v: string): string | undefined => {
  if (!v) return undefined;
  // Accept date-only (yyyy-MM-dd) or datetime (yyyy-MM-ddTHH:mm[:ss]); time is dropped —
  // all date fields in the form are type="date" (bug #19).
  const d = new Date(v.includes('T') ? `${v.slice(0, 10)}T00:00:00Z` : `${v}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

// ---------------------------------------------------------------------------
// Form ↔ Campaign conversion
// ---------------------------------------------------------------------------

/**
 * Keys rendered by the Partner Send section — stripped from `crmConfigRest`
 * on load so the rest never carries a rendered value (034-01 decision 3).
 * Includes both canonical names and the five as-built alias keys so that alias
 * rows are also silently dropped: old rows read through normalizeCrmConfig(),
 * and the next save rewrites them canonical.
 */
const CRM_RENDERED_KEYS = new Set([
  // Canonical rendered keys (§2.3.1)
  'send_mode',
  'method',
  'placeholder_syntax',
  'get_url',
  'post_url',
  'direct_url',
  // As-built alias keys — also stripped so a row never carries two spellings
  'sendMethod',
  'sendSyntax',
  'getCrmUrl',
  'directCrmUrl',
  'postCrmUrl',
  // ADR-016 §9's carve-out from HF-019's round-trip (034-02): these two are
  // rendered keys of the Send via switch, not part of the unrendered rest —
  // they must never ride along, because omission is how a Simple URL save
  // clears them under the server's whole-object replace.
  'endpoint_id',
  'values',
  // 034-03: now rendered as credentialId/authRest and phrase string fields.
  // Stripping them from crmConfigRest prevents a double-write in Simple URL mode
  // (the saver reconstructs them from the form fields) and prevents them from
  // riding through to the endpoint-mode payload via the rest spread (the
  // INLINE_TRANSPORT_KEYS strip in crmConfigFromForm is a secondary defence).
  'auth',
  'response_rules',
]);

/**
 * The five as-built alias keys — `CRM_CONFIG_ALIASES` in
 * `packages/shared/src/domain/campaign/crm-config.schema.ts`. Named here
 * because an endpoint-mode payload deletes them alongside
 * `INLINE_TRANSPORT_KEYS` (ADR-016 §2), which carries only four of them
 * (`sendSyntax` survives the server's strip; the form writes the canonical
 * `placeholder_syntax` instead, so no row keeps two spellings).
 */
const CRM_CONFIG_ALIAS_KEYS = [
  'getCrmUrl',
  'postCrmUrl',
  'directCrmUrl',
  'sendMethod',
  'sendSyntax',
] as const;

/**
 * `crm_config.values` → form state (034-02): a record of literal strings keyed
 * by the endpoint's `variable.key` (ADR-016 §3). Non-string scalars of a
 * hand-written row are shown as their text; a nested object or array is not a
 * value the schema allows and is dropped rather than rendered as `[object
 * Object]`.
 */
function readEndpointValues(raw: unknown): Record<string, string> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string') values[key] = value;
    else if (typeof value === 'number' || typeof value === 'boolean') values[key] = String(value);
  }
  return values;
}

function formFromCampaign(c: CampaignDetailResponse): FormState {
  const f: FormState = { ...EMPTY_FORM };

  // Read campaignStyle from response (server backfilled all rows).
  // Fall back to deriving from flowKind for any legacy data.
  const campaignStyle = c.campaignStyle ?? null;
  if (campaignStyle) {
    f.style = campaignStyleEnumToLabel(campaignStyle);
    f.campaignStyle = campaignStyle;
  } else {
    // Legacy fallback: derive from flowKind + capture_strategy
    if (c.flowKind === 'coreg') {
      f.style = 'Co-op';
      f.campaignStyle = 'co_op';
    } else if (c.flowKind === 'hlp' && c.captureStrategy === 'parameter_capture') {
      f.style = 'CTO Email';
      f.campaignStyle = 'cto_email';
    } else if (c.flowKind === 'hlp') {
      // Default hlp to paid_social if it has capture_strategy, otherwise custom_optin
      f.style = c.captureStrategy ? 'Paid Social' : 'Custom Opt-in';
      f.campaignStyle = c.captureStrategy ? 'paid_social' : 'custom_optin';
    }
  }
  f.name = c.name ?? '';
  f.advertiserId = c.advertiserId;
  f.clientName = c.clientName ?? '';
  f.status = c.status;
  // 054-05: display-only round-trip — the sub-label under the status pill.
  f.statusReason = c.statusReason ?? null;
  f.usOnly = c.usOnly ?? false;

  f.pricePerLead = c.pricePerLead != null ? String(c.pricePerLead) : '';
  // 066 D4: the routing pin. A stored value hydrates as its string; null /
  // undefined (no pin, or the column's NULL) hydrate '' — the pricePerLead model.
  f.priority = c.priority != null ? String(c.priority) : '';
  f.limitType = c.limitType ?? '';
  f.limitValue = c.limitValue != null ? String(c.limitValue) : '';
  f.dailyCap = c.dailyCap != null ? String(c.dailyCap) : '';
  f.maxFacebookSharePct = c.maxFacebookSharePct != null ? String(c.maxFacebookSharePct) : '';

  f.startDate = ISO_TO_DATE(c.startDate);
  f.endDate = ISO_TO_DATE(c.endDate);

  // HF-154: the field is a single-line <Input>, which strips '\n'.
  f.domainRestrictions = (c.domainRestrictions ?? []).join(', ');
  f.restrictedSiteIds = (c.siteRestrictions ?? []).map((s) => s.id);

  f.suppressionEnabled = c.suppression?.enabled ?? false;
  f.suppressionPrepingUrl = c.suppression?.prepingApiUrl ?? '';
  f.suppressionFileId = c.suppression?.suppressionFileId ?? '';
  f.suppressionPendingFile = null; // Clear pending file on Edit mode
  // #49: the flag only rides the campaigns read payload; a response that lacks it
  // (create/update/lifecycle return the bare aggregate) reads as "no signal".
  f.suppressionFileUnreadable = c.suppressionFileUnreadable === true;

  f.pageSlug = c.pageSlug ?? '';
  f.pageTitle = c.pageTitle ?? '';
  // FR-A37 (026-02); style-aware since 054-03: a stored NULL (the mapper maps
  // it to undefined) on a custom_optin row hydrates to '' — the field is
  // editable there now, and falling back to the built-in default would re-save
  // it on the next Update and undo the operator's emptied field. Every other
  // style falls back to the built-in disclaimer, so legacy rows open
  // pre-filled with today's copy; a stored non-null value hydrates verbatim
  // for every style.
  f.optInDisclaimer =
    c.optInDisclaimer ?? (f.campaignStyle === 'custom_optin' ? '' : OPT_IN_DISCLAIMER_DEFAULT);
  f.ctaText = c.ctaText ?? ''; // nullable column → form string (epic 042)
  // Epic 106: NULL/'default' both mean the standard look → the form's ''.
  f.offerTemplate = c.template === 'partner-hero-v1' ? 'partner-hero-v1' : '';
  f.headline = c.headline ?? '';
  f.introHtml = c.introHtml ?? '';
  f.htmlContent = c.htmlContent ?? '';
  f.thankYouMessage = c.thankYouMessage ?? THANK_YOU_DEFAULT;
  f.useAsTemplate = c.useAsTemplate ?? false; // nullable column → form boolean
  f.gaEventLabel = c.gaEventLabel ?? ''; // nullable column → form string
  // A14 (034-05): null = style default, true = on, false = off.
  f.capiEnabled = c.capiEnabled === true ? 'on' : c.capiEnabled === false ? 'off' : 'default';

  f.captureStrategy = c.captureStrategy ?? ''; // Empty string for null (non-journey styles)
  f.consentBasis = c.consentBasis ?? 'on_page';
  const consentCfg = (c.consentConfig ?? {}) as Record<string, unknown>;
  f.consentEvidence = typeof consentCfg.evidence === 'string' ? consentCfg.evidence : '';

  // Rows keep the server order (response is already ord-asc); display order =
  // array order = execution order (#42 variant B). No ord in row state — it is
  // position-derived at save time.
  f.eligibilitySteps = (c.eligibilitySteps ?? []).map((s) => ({
    id: newRowId(),
    stepKind: s.stepKind,
    configJson: JSON.stringify(s.config ?? {}, null, 2),
  }));

  f.espDestinations = (c.espDestinations ?? []).map((d) => ({
    id: newRowId(),
    provider: d.provider,
    brandList: d.brandList,
    listIdentifier: d.listIdentifier ?? '',
    fieldMappingsJson: JSON.stringify(d.fieldMappings ?? {}, null, 2),
    // A12 (034-05): transactional pair — null from the server becomes '' in
    // form state; the pair is set on the Maropost row only.
    transactionalCampaignId: d.transactionalCampaignId ?? '',
    transactionalOn: (d.transactionalOn ?? '') as '' | 'success' | 'valid',
    // 067-06: the TT row's journey + tags inputs, read from fieldMappings as
    // stored — no default here (an existing row's empty id stays empty).
    ...maropostInputsFrom(d.fieldMappings),
  }));

  // CRM config → Partner Send fields (034-01 A1).
  // normalizeCrmConfig: canonical wins over alias (ADR-010 A1); canonical keys +
  // five aliases are stripped from crmConfigRest so a row never carries two spellings.
  const rawCrm = (c.crmConfig ?? {}) as Record<string, unknown>;
  try {
    const normalized = normalizeCrmConfig(rawCrm);
    f.sendMode = normalized.send_mode ?? 'lead_post';
    f.sendMethod = normalized.method ?? 'GET';
    f.sendSyntax = normalized.placeholder_syntax ?? 'percent';
    // POST rows: prefer post_url, fall back to get_url (mirrors request-builder.ts:327–333)
    f.sendUrl =
      f.sendMethod === 'POST'
        ? (normalized.post_url ?? normalized.get_url ?? '')
        : (normalized.get_url ?? '');
    f.directCpcUrl = normalized.direct_url ?? '';
  } catch {
    // Stored row has an invalid enum value — fall back to raw keys so the form
    // still opens; the engine rejects the row at send time, not in the admin.
    const toStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
    f.sendMode = toStr(rawCrm.send_mode) ?? 'lead_post';
    f.sendMethod = toStr(rawCrm.method ?? rawCrm.sendMethod) ?? 'GET';
    f.sendSyntax = toStr(rawCrm.placeholder_syntax ?? rawCrm.sendSyntax) ?? 'percent';
    const rawGetUrl = toStr(rawCrm.get_url ?? rawCrm.getCrmUrl);
    const rawPostUrl = toStr(rawCrm.post_url ?? rawCrm.postCrmUrl);
    f.sendUrl = f.sendMethod === 'POST' ? (rawPostUrl ?? rawGetUrl ?? '') : (rawGetUrl ?? '');
    f.directCpcUrl = toStr(rawCrm.direct_url ?? rawCrm.directCrmUrl) ?? '';
  }
  // Partner Send mode (034-02, ADR-016 §2): derived from the stored row, never
  // stored itself — endpoint_id present ⇒ Partner endpoint mode. Both keys are
  // read off the raw object: neither has an alias, so the normalizer passes
  // them through unchanged and the catch branch above agrees with the try.
  const rawEndpointId = rawCrm.endpoint_id;
  f.endpointId =
    rawEndpointId === undefined || rawEndpointId === null || rawEndpointId === ''
      ? ''
      : String(rawEndpointId);
  f.sendVia = f.endpointId === '' ? 'simple' : 'endpoint';
  f.endpointValues = readEndpointValues(rawCrm.values);
  const crmRest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rawCrm)) {
    if (!CRM_RENDERED_KEYS.has(k)) crmRest[k] = v;
  }
  f.crmConfigRest = crmRest;

  // auth → credentialId + authRest (034-03): credential_id moves into a
  // discrete string field; every other auth key (header, param, etc.) is
  // kept in authRest so a round-trip save rebuilds the full auth object.
  const rawAuth = rawCrm.auth;
  if (typeof rawAuth === 'object' && rawAuth !== null && !Array.isArray(rawAuth)) {
    const authObj = rawAuth as Record<string, unknown>;
    const { credential_id: rawCredId, ...restOfAuth } = authObj;
    f.credentialId = rawCredId != null ? String(rawCredId) : '';
    f.authRest = restOfAuth;
  } else {
    f.credentialId = '';
    f.authRest = {};
  }
  // response_rules → phrase strings (034-03): each array is joined with ', ';
  // an absent key yields '' so the field appears blank (which renders the
  // default placeholder and omits the key on save — ADR-010 §6's defaults apply).
  const rawRules = rawCrm.response_rules;
  const rulesObj =
    typeof rawRules === 'object' && rawRules !== null && !Array.isArray(rawRules)
      ? (rawRules as Record<string, unknown>)
      : {};
  const joinArr = (v: unknown): string =>
    Array.isArray(v)
      ? (v as unknown[]).filter((s): s is string => typeof s === 'string').join(', ')
      : '';
  f.acceptedContains = joinArr(rulesObj.accepted_contains);
  f.duplicateContains = joinArr(rulesObj.duplicate_contains);
  f.rejectedContains = joinArr(rulesObj.rejected_contains);

  f.placementSequence = c.placement?.sequence != null ? String(c.placement.sequence) : '';
  f.placementSpot = c.placement?.spot != null ? String(c.placement.spot) : '';
  f.placementIsThankYouAnchor = c.placement?.isThankYouAnchor ?? false;
  if (c.placement) {
    f.loadedPlacement = {
      sequence: c.placement.sequence ?? null,
      spot: c.placement.spot ?? null,
      isThankYouAnchor: c.placement.isThankYouAnchor ?? false,
    };
  }

  f.cmEnabled = c.cmConfig?.enabled ?? false;
  f.cmDomain = c.cmConfig?.domain ?? '';
  f.cmName = c.cmConfig?.name ?? '';
  f.cmSlug = c.cmConfig?.slug ?? '';

  f.bannerImageUrl = c.bannerImageUrl ?? '';

  // Epic 096 task 03 (D4/D5a): nullable columns → form strings; '' = not set.
  f.errorUrl = c.errorUrl ?? '';
  f.siteId = c.siteId ?? '';
  // Epic 109 task 01: same nullable-column → form-string hydration.
  f.fbCampaignId = c.fbCampaignId ?? '';

  return f;
}

/**
 * The transport mode a save actually applies (034-02, ADR-016 §2).
 *
 * `f.sendVia` is what the operator picked; endpoint mode is **lead-post only**
 * and lives in the Partner Send section, so a click-out Custom Opt-in — or a
 * style with no Partner Send at all — is always Simple URL whatever the radio
 * holds. This is the one reader of that rule: the payload and the switch
 * banners must never disagree about which mode a save is in.
 */
function effectiveSendVia(
  f: FormState,
  flags: ReturnType<typeof useStyleFlags>
): 'simple' | 'endpoint' {
  return f.sendVia === 'endpoint' && flags.hasPartnerSend && f.sendMode !== 'click_out'
    ? 'endpoint'
    : 'simple';
}

/**
 * `crm_config.values` for the payload (034-02): a blank field means "supply
 * nothing", so the key is omitted rather than sent as `''`. Omission is
 * exactly the condition under which the engine falls back to the endpoint's
 * `default` (`validateEndpointValues`, ADR-016 §3), which is what the blank
 * field shows as its placeholder — and it makes the client's required-blank
 * pre-check and the server's "missing" verdict answer identically.
 */
function endpointValuesForPayload(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value.trim() !== '') out[key] = value;
  }
  return out;
}

/**
 * Build the canonical `crm_config` payload from form state (034-01 A1,
 * extended by 034-02 with the two mode payload rules of ADR-016 §2).
 *
 * **Simple URL** — canonical §2.3.1 keys only, alias keys never written, the
 * URL under the method's key; `endpoint_id` / `values` deleted outright. Under
 * the server's whole-object replace omission *is* clearing, so the typed URL
 * governs and nothing on the write path strips anything (ADR-016 §2,
 * off-endpoint direction).
 *
 * **Partner endpoint** — `endpoint_id` (as a number) + `values`, with every
 * inline transport key and every alias deleted: the endpoint's transport is
 * the view and the campaign's inline keys are ignored, so the form never
 * resurrects a stale URL even if the server's own strip were absent (ADR-016
 * §2, onto-endpoint direction; epic README decision 2). `send_mode` and
 * `placeholder_syntax` stay campaign-level in both modes.
 */
function crmConfigFromForm(
  f: FormState,
  flags: ReturnType<typeof useStyleFlags>
): Record<string, unknown> {
  const config: Record<string, unknown> = { ...f.crmConfigRest };
  // Campaign-level in both modes (ADR-016 §2 keeps placeholder_syntax out of
  // the strip — the campaign-level pre-ping reads the campaign's syntax).
  config.send_mode = f.sendMode;
  config.placeholder_syntax = f.sendSyntax;
  // Skip Duplicates (epic 057 FR-B35 / ADR-010 A43, handed to this epic — 057
  // README "The split with 054"): the checkbox state lives in the rest bag
  // (HF-019) — the loader captures a stored key here (skip_duplicates is not
  // in CRM_RENDERED_KEYS) and the checkbox's onChange writes it back. The save
  // always writes an explicit boolean for Partner Send campaigns, so a
  // flag-off row is distinguishable from a legacy key-absent row (the engine
  // reads both as off — 057 README decision 8). The gate mirrors the section
  // (hasPartnerSend = Co-op + Custom Opt-in).
  if (flags.hasPartnerSend) {
    config.skip_duplicates = f.crmConfigRest?.skip_duplicates === true;
  }

  if (effectiveSendVia(f, flags) === 'endpoint') {
    for (const key of INLINE_TRANSPORT_KEYS) delete config[key];
    for (const key of CRM_CONFIG_ALIAS_KEYS) delete config[key];
    config.endpoint_id = Number(f.endpointId ?? '');
    config.values = endpointValuesForPayload(f.endpointValues ?? {});
    return config;
  }

  // Neither key can be in the rest — the loader strips both (CRM_RENDERED_KEYS)
  // — so these deletes only assert the carve-out against a future rest writer.
  delete config.endpoint_id;
  delete config.values;
  config.method = f.sendMethod;
  if (f.sendMethod === 'GET') {
    if (f.sendUrl.trim()) config.get_url = f.sendUrl.trim();
    delete config.post_url; // cannot be in rest after 034-01 loader; defensive
  } else {
    if (f.sendUrl.trim()) config.post_url = f.sendUrl.trim();
    delete config.get_url; // cannot be in rest after 034-01 loader; defensive
  }
  // HF-083: lead post also carries an optional click destination (the lead
  // is still posted server-side; the browser's click target is separate) —
  // write it whenever non-empty, same as click-out, so a round-trip save
  // does not drop a stored direct_url (it is a CRM_RENDERED_KEYS member,
  // stripped from the unrendered rest).
  if (
    flags.isOptin &&
    (f.sendMode === 'click_out' || f.sendMode === 'lead_post') &&
    f.directCpcUrl.trim()
  ) {
    config.direct_url = f.directCpcUrl.trim();
  }

  // auth (034-03, Simple URL only): reconstruct from credentialId + authRest.
  // credential_id is written as a number when a credential is chosen; the other
  // auth props (header, param, etc.) ride through via authRest so a round-trip
  // save never loses them. The key is omitted when both are empty so the server
  // falls back to unauthenticated (ADR-010 §6).
  const credId = f.credentialId ?? '';
  const authRest = f.authRest ?? {};
  if (credId) {
    config.auth = { ...authRest, credential_id: Number(credId) };
  } else if (Object.keys(authRest).length > 0) {
    config.auth = { ...authRest };
  }

  // response_rules (034-03, Simple URL only): split each phrase string on
  // commas, trim and drop blanks; omit the key entirely when all three are
  // empty so ADR-010 §6's defaults apply.
  const parsePhrase = (s: string): string[] =>
    s
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  const accepted = parsePhrase(f.acceptedContains ?? '');
  const duplicate = parsePhrase(f.duplicateContains ?? '');
  const rejected = parsePhrase(f.rejectedContains ?? '');
  if (accepted.length > 0 || duplicate.length > 0 || rejected.length > 0) {
    const rules: Record<string, string[]> = {};
    if (accepted.length > 0) rules.accepted_contains = accepted;
    if (duplicate.length > 0) rules.duplicate_contains = duplicate;
    if (rejected.length > 0) rules.rejected_contains = rejected;
    config.response_rules = rules;
  }

  return config;
}

function aggregateFromForm(f: FormState): CampaignAggregateCreateInput {
  const parseJson = (raw: string, path: string): Record<string, unknown> => {
    if (!raw.trim()) return {};
    try {
      const v = JSON.parse(raw);
      return typeof v === 'object' && v !== null && !Array.isArray(v)
        ? (v as Record<string, unknown>)
        : {};
    } catch {
      const err = new Error('Invalid JSON');
      (err as Error & { field?: string }).field = path;
      throw err;
    }
  };

  const num = (v: string): number | undefined => {
    if (!v.trim()) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const int = (v: string): number | undefined => {
    const n = num(v);
    return n == null ? undefined : Math.trunc(n);
  };

  const domainRestrictions = f.domainRestrictions
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const flowKind = styleToFlowKind(f.style);
  const flags = useStyleFlags(f.style);

  // Canonical crm_config payload — canonical §2.3.1 keys only (034-01 A1).
  const crmConfig = crmConfigFromForm(f, flags);

  const payload: CampaignAggregateCreateInput = {
    name: f.name,
    campaignStyle: f.campaignStyle, // Send campaignStyle in payload (phase-2 of three-phase deploy)
    flowKind,
    subType: 'regular',
    status: f.status as CampaignStatus,
    captureNamePhone: false,
    usOnly: f.usOnly,
    captureStrategy: flags.isJourney
      ? (f.captureStrategy as CampaignAggregateCreateInput['captureStrategy'])
      : undefined, // Omit for non-journey styles (README #5)
    consentBasis: flags.isJourney
      ? (f.consentBasis as CampaignAggregateCreateInput['consentBasis'])
      : undefined, // Omit for non-journey styles
    consentConfig: flags.isJourney
      ? f.consentEvidence.trim()
        ? { evidence: f.consentEvidence.trim() }
        : {}
      : undefined, // Omit for non-journey styles
    crmConfig,
    espDestinations: flags.hasEspDestinations ? [] : [], // Empty array for styles without the section, populated below
    eligibilitySteps: flags.isJourney ? [] : [], // Empty array for non-journey, populated below for journey
    siteRestrictions: [],
  };

  // Epic 043 (task 01): a CTO Email campaign names no advertiser — the links
  // are ours, and the server records the internal TradingTips owner. The key
  // is omitted for cto_email so the hidden field's (empty) value never rides
  // the wire; every other style keeps sending the picked advertiser.
  if (!flags.isCtoEmail) payload.advertiserId = f.advertiserId;
  if (f.clientName.trim()) payload.clientName = f.clientName;
  if (f.pricePerLead.trim()) {
    const p = num(f.pricePerLead);
    if (p != null) payload.pricePerLead = p;
  }
  // 066 D4: the Co-op routing pin rides this style only. A set pin is the
  // integer; an empty field is omitted here (create has nothing to clear) and
  // turned into an explicit null by the update path in submit (D4a), so a
  // stored pin can be removed. Non-Co-op styles never send priority.
  if (flags.isCoop) {
    const pr = int(f.priority ?? '');
    if (pr != null) payload.priority = pr;
  }
  // Co-op campaigns always use unique_leads limit type (task 015-04)
  if (flags.isCoop) {
    payload.limitType = 'unique_leads';
    const lv = int(f.limitValue);
    if (lv != null) payload.limitValue = lv;
  } else if (f.limitType) {
    payload.limitType = f.limitType as CampaignAggregateCreateInput['limitType'];
    const lv = int(f.limitValue);
    if (lv != null) payload.limitValue = lv;
  }
  const dc = int(f.dailyCap);
  if (dc != null) payload.dailyCap = dc;
  // Epic 091 D9: Co-op ALWAYS carries the share (an integer, or null for an empty
  // field), so a cleared field clears on update — an absent key would mean "leave
  // unchanged". Validation in submit has already refused a non-integer. Other
  // styles never send the key, so a stored value round-trips untouched.
  if (flags.isCoop) payload.maxFacebookSharePct = int(f.maxFacebookSharePct ?? '') ?? null;

  const startIso = DATE_TO_ISO(f.startDate);
  if (startIso) payload.startDate = new Date(startIso);
  const endIso = DATE_TO_ISO(f.endDate);
  if (endIso) payload.endDate = new Date(endIso);

  if (domainRestrictions.length > 0) payload.domainRestrictions = domainRestrictions;

  // Offer page fields for hosted-page campaigns
  if (flags.hasHostedPage) {
    if (f.pageSlug.trim()) payload.pageSlug = f.pageSlug;
    if (f.pageTitle.trim()) payload.pageTitle = f.pageTitle;
    // Opt-in disclaimer (FR-A37 / 026-02): ALWAYS included, unlike pageTitle —
    // a trimmed-empty value means "clear" (the api stores NULL, which restores
    // the built-in fallback on the landing page). The value itself is HTML and
    // is sent untrimmed.
    payload.optInDisclaimer = f.optInDisclaimer;
    // Offer CTA label (epic 042 / 037-10): ALWAYS included, mirroring the
    // disclaimer — a trimmed-empty value means "clear" (the api stores NULL,
    // which renders the platform default label). The field renders for Custom
    // Opt-in (offer Accept box) and Paid Social (welcome submit button); on
    // hidden styles the stored value still round-trips untouched (same
    // visibility-only semantics as the disclaimer above).
    payload.ctaText = f.ctaText.trim();
    // Epic 106 (ruling 2026-10-20): the offer-page template discriminator —
    // sent only for Custom Opt-in while the Partner hero template is
    // selected; absent = the default look (the old payload shape,
    // byte-identical for Standard and for every other style — a stored
    // template on a style-switched row never leaks onto their payloads).
    // Stored template-specific values survive a switch back to Standard
    // (accepted quirk, D8 amendment).
    if (flags.isOptin && f.offerTemplate === 'partner-hero-v1') {
      payload.template = f.offerTemplate;
    }
    // Template-specific fields (epic 106): rendered under Partner hero only,
    // sent only when non-empty (empty ⇒ key omitted — "leave unchanged" on
    // update, nothing to store on create). The stored values of a campaign
    // switched back to Standard round-trip untouched.
    if (flags.isOptin && f.offerTemplate === 'partner-hero-v1' && f.headline.trim()) {
      payload.headline = f.headline;
    }
    if (flags.isOptin && f.offerTemplate === 'partner-hero-v1' && f.introHtml.trim()) {
      payload.introHtml = f.introHtml;
    }
    if (f.htmlContent) payload.htmlContent = f.htmlContent;
    if (f.thankYouMessage.trim()) payload.thankYouMessage = f.thankYouMessage;
    // Toggle state rides the ordinary save payload both ways; hidden sections
    // never contribute — Co-op rows (no hosted page) omit the field entirely.
    payload.useAsTemplate = f.useAsTemplate; // column round-trip only — UI removed 037-03 (Chelsie Sep-4)
    // GA click label (FR-A32 / 019-04): stored verbatim up to 255 chars; blank =
    // no GA click events fired anywhere for this campaign. Legacy evidence backs
    // the Co-op exclusion (Co-op source tables carry no ga column).
    if (f.gaEventLabel.trim()) payload.gaEventLabel = f.gaEventLabel.trim();
  }

  // A14 (034-05): Meta CAPI switch. Sent for all non-Co-op styles (the
  // Tracking section renders for hasHostedPage = !isCoop). Null is always
  // sent so an explicit value can be reset to the style default.
  if (!flags.isCoop) {
    payload.capiEnabled = f.capiEnabled === 'on' ? true : f.capiEnabled === 'off' ? false : null;
  }

  // Custom Opt-in placement is server-owned (epic 083 D6) — the payload never
  // carries a `placement`; the server places the page on Schedule/Start.

  // Journey primitives — only include for journey styles (README #4). The
  // eligibility steps are Paid Social + External Form since epic 024 (task 04)
  // / 096 (task 03): CTO Email has no per-campaign eligibility section (its
  // validations live on the link rows), so the hidden section contributes an
  // empty array on every save exactly as the delivery surfaces do.
  if (flags.isJourney) {
    payload.eligibilitySteps =
      flags.isPaidSocial || flags.isExternalForm
        ? f.eligibilitySteps.map((row, index) => ({
            stepKind: row.stepKind,
            ord: index, // Position-derived (0-based); card order is the ordering control (#42)
            config: parseJson(row.configJson, `eligibilitySteps.${row.id}.config`),
          }))
        : [];
  }

  if (flags.hasEspDestinations) {
    payload.espDestinations = f.espDestinations.map((row) => ({
      provider: row.provider as CampaignAggregateCreateInput['espDestinations'][number]['provider'],
      brandList:
        row.brandList as CampaignAggregateCreateInput['espDestinations'][number]['brandList'],
      listIdentifier: row.listIdentifier.trim() || undefined,
      fieldMappings: withMaropostTTConfig(
        parseJson(row.fieldMappingsJson, `espDestinations.${row.id}.fieldMappings`),
        row
      ),
    }));
  }

  payload.siteRestrictions = f.restrictedSiteIds.map((id) => ({
    id,
  }));

  payload.suppression = {
    enabled: f.suppressionEnabled,
    hashAlgorithm: 'md5',
    prepingApiUrl: f.suppressionPrepingUrl.trim(),
  };

  payload.cmConfig = {
    enabled: f.cmEnabled,
    domain: f.cmDomain.trim() || undefined,
    name: f.cmName.trim() || undefined,
    slug: f.cmSlug.trim() || undefined,
  };

  // Creative image for campaigns that present an offer (Headline + Offer body
  // removed from the wire too — FR-A28 v0.18, HF-079; stored values stay, inert)
  if (flags.presentsOffer) {
    if (f.bannerImageUrl.trim()) payload.bannerImageUrl = f.bannerImageUrl;
  }
  // Custom Opt-in's page image (epic 082): ALWAYS included, trimmed — a
  // trimmed-empty value means "clear" (the api stores NULL, the offer page
  // renders without an image). Other styles send nothing here.
  if (flags.isOptin) {
    payload.bannerImageUrl = f.bannerImageUrl.trim();
  }

  // Epic 096 task 03 (D4/D5a): the `/ext` interstitial settings ride the
  // External Form branch ONLY, ALWAYS with an explicit value — null is what
  // an emptied field means on update (the 091 maxFacebookSharePct pattern:
  // an absent key would read "leave unchanged" and a cleared field could
  // never clear). Other styles never send the keys, so a stored value on a
  // style-switched row round-trips untouched.
  if (flags.isExternalForm) {
    payload.errorUrl = (f.errorUrl ?? '').trim() || null;
    payload.siteId = f.siteId || null;
    // FB campaign mapping (epic 109 task 01): trimmed value, or null when
    // emptied — an absent key would mean "leave unchanged" and a cleared
    // field could never clear (the 091 maxFacebookSharePct pattern). No
    // style gate beyond this section: the write schema accepts the key for
    // every style (ruling 1) — other styles simply don't send it here.
    payload.fbCampaignId = (f.fbCampaignId ?? '').trim() || null;
    // D7b (task 07): ALWAYS an explicit string for this style — never null on
    // the wire (the write schema is z.string().optional()). An emptied field
    // sends '', which the api's thankYouMessageToPersist normalizer persists
    // as NULL (= no confirmation bar). Other styles keep the :1337
    // send-non-empty idiom above — byte-identical behavior for them.
    payload.thankYouMessage = f.thankYouMessage ?? '';
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Preview payload (epic 037 task 05)
//
// The Preview button renders the UNSAVED form state as a landing page: the
// aggregate payload gets the response-shape stubs the form does not carry
// (ids, timestamps, publisherId, child-row bookkeeping), is gated on
// `campaignResponseSchema.safeParse`, then POSTed for a short-lived token —
// the token, never the payload, rides the preview URL (htmlContent runs to
// tens of KB; a JSON query param would 414/400).
// ---------------------------------------------------------------------------

// Fixed placeholder stamps/ids for the row bookkeeping an unsaved form has no
// value for (operator-confirmed stubs suffice — the /preview route renders by
// style and content, never by row identity).
const PREVIEW_STUB_ISO = '1970-01-01T00:00:00.000Z';
const PREVIEW_STUB_ID = 'preview';

export type PreviewCampaignResult =
  { ok: true; campaign: CampaignResponse } | { ok: false; reason: string };

/**
 * Build + validate the preview payload from the current form state.
 * Exported as the test seam for the schema-parity unit tests (the form tests
 * import only CampaignForm otherwise).
 */
/**
 * The form-state shape `previewCampaignFromForm` accepts: the module-local
 * FormState with every key optional — its out-of-epic FormState-literal
 * builders keep compiling as each epic's additions land; EMPTY_FORM supplies
 * every default. (`headline` re-widens to `string | undefined`: the form
 * happens to share the name with an epic-095 response field that some
 * builders carry as such.)
 */
export type PreviewFormState = { [K in keyof FormState]?: FormState[K] | undefined };

export function previewCampaignFromForm(f: PreviewFormState): PreviewCampaignResult {
  // The module-local FormState has grown required keys across tasks while
  // this file's out-of-epic FormState-literal builders keep compiling
  // (PreviewFormState): EMPTY_FORM supplies every omitted key's default, so
  // the payload shape stays exactly what the running form would send.
  const complete: FormState = { ...EMPTY_FORM, ...f, headline: f.headline ?? '' };
  let payload: CampaignAggregateCreateInput;
  try {
    payload = aggregateFromForm(complete);
  } catch (err) {
    // aggregateFromForm throws on unparseable row JSON, carrying the row path.
    const e = err as Error & { field?: string };
    return { ok: false, reason: e.field ? `${e.field}: ${e.message}` : e.message };
  }

  // Dates ride the write payload as Date-union objects; the response shape
  // wants ISO strings (or absent) — drop the two run-window keys and re-derive
  // them from the form (scheduleDate is overridden to absent in the literal).
  const rest = { ...payload };
  delete rest.startDate;
  delete rest.endDate;

  // The shared aggregate-create child schemas are .partial() (campaignId is
  // service-set), so their element keys read optional to TS — but
  // aggregateFromForm just constructed every one of them with a value, and the
  // response shape requires those keys. The single targeted casts below only
  // re-assert what was built two lines above (never a double-cast, house rule).
  const stubbed = <K extends { id?: unknown }>(element: K) => ({
    ...element,
    id: PREVIEW_STUB_ID,
    campaignId: PREVIEW_STUB_ID,
    createdAt: PREVIEW_STUB_ISO,
    updatedAt: PREVIEW_STUB_ISO,
  });

  const campaign: CampaignResponse = {
    ...rest,
    id: PREVIEW_STUB_ID,
    advertiserId: complete.advertiserId,
    // The write contract omits publisherId (server-assigned); the read shape
    // requires it — stub.
    publisherId: '0',
    // status/flowKind/subType/usOnly/captureNamePhone are server-defaulted
    // optional keys on the write schema (.partial in the aggregate create);
    // the response requires all of them. Re-derive each exactly as the
    // payload did.
    flowKind: styleToFlowKind(complete.style),
    status: complete.status as CampaignStatus,
    subType: 'regular',
    usOnly: complete.usOnly,
    captureNamePhone: false,
    startDate: DATE_TO_ISO(complete.startDate),
    endDate: DATE_TO_ISO(complete.endDate),
    scheduleDate: undefined, // the form never carries it; response wants string|undefined
    createdAt: PREVIEW_STUB_ISO,
    updatedAt: PREVIEW_STUB_ISO,
    suppression: payload.suppression
      ? ({
          ...stubbed(payload.suppression),
          suppressionFileId: null,
        } as NonNullable<CampaignResponse['suppression']>)
      : null,
    cmConfig: payload.cmConfig
      ? (stubbed(payload.cmConfig) as NonNullable<CampaignResponse['cmConfig']>)
      : null,
    placement: payload.placement
      ? (stubbed(payload.placement) as NonNullable<CampaignResponse['placement']>)
      : undefined,
    espDestinations: payload.espDestinations.map(
      (d) => stubbed(d) as CampaignResponse['espDestinations'][number]
    ),
    eligibilitySteps: payload.eligibilitySteps.map(
      (s) => stubbed(s) as CampaignResponse['eligibilitySteps'][number]
    ),
    // The response element adds the site's business key; an unsaved draft has
    // no joined rows to read it from.
    siteRestrictions: complete.restrictedSiteIds.map((id) => ({ id, siteId: '' })),
  };

  const parsed = campaignResponseSchema.safeParse(campaign);
  if (!parsed.success) {
    return {
      ok: false,
      reason: parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; '),
    };
  }
  return { ok: true, campaign: parsed.data };
}

// ---------------------------------------------------------------------------
// Slugify helper (task 015-09)
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumerics (except dash/space)
    .replace(/\s+/g, '-') // Spaces → dashes
    .replace(/-+/g, '-') // Collapse multiple dashes
    .replace(/^-|-$/g, ''); // Trim leading/trailing dashes
}

// ---------------------------------------------------------------------------
// Fresh-clone navigation state (task 023-08; query channel 069-03 D5)
//
// Duplicate lands on the new campaign's edit screen as a fresh clone via
// EITHER channel: the legacy router history state
// `{ state: { freshClone: true } }` or the `?freshClone=1` query param
// (campaigns.tsx's ?clone= mount flow — state cannot survive a new tab). On
// that origin the loaded "<old-slug>-copy" slug is NOT treated as
// hand-edited, so the first page-title edit regenerates the whole slug for
// the new month.
// ---------------------------------------------------------------------------

// Narrow the react-router history state without unsafe casts (task 023-08).
// The `in` check narrows `object` to `object & Record<'freshClone', unknown>`,
// so no type assertion is needed to read the flag.
function readFreshCloneState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'freshClone' in state &&
    state.freshClone === true
  );
}

// ---------------------------------------------------------------------------
// Lifecycle button logic
//
// Two buttons:
//   1. start / schedule / pause  — label depends on status + startDate vs now
//   2. complete                  — always labeled "Complete"
//
// Default status is 'pending'. Buttons also disable on terminal status
// (completed). The server still enforces legal transitions and surfaces any
// rejection as a toast.
// ---------------------------------------------------------------------------

// Parse the form's date-only string (yyyy-MM-dd) to a UTC millisecond timestamp.
// Returns undefined for empty/invalid input.
const DATE_TO_MS = (v: string): number | undefined => {
  if (!v) return undefined;
  const d = new Date(v.includes('T') ? `${v.slice(0, 10)}T00:00:00Z` : `${v}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.getTime();
};

// The client's day for the run-window end bound (HF-152) — the leads.tsx
// CLIENT_TIME_ZONE idiom. The admin and the api's DISTRIBUTION_TZ both run
// on America/New_York (config default).
const RUN_WINDOW_TIME_ZONE = 'America/New_York';

/**
 * The end bound `primaryButtonState`'s window check receives (HF-152). An
 * end date means "through 23:59 ET of that day" — the server's
 * `isWithinCampaignDays` half-open ET-day rule (HF-103) — so the form must
 * not compare it as the 00:00 UTC instant `DATE_TO_MS` yields, which ends
 * the window 20:00 ET the evening before (the same defect reopen() carried).
 * While the stored end date is today-or-later in ET the end side is open
 * (undefined); once the ET day is over it is a past instant and the shared
 * `isWithinRunWindow` keeps Start disabled. `endMs` is undefined = unbounded,
 * so an empty/invalid end date behaves exactly as before.
 */
function endBoundMs(endDate: string, now: Date): number | undefined {
  const endMs = DATE_TO_MS(endDate);
  if (endMs === undefined) return undefined;
  const endDay = endDate.slice(0, 10);
  const today = now.toLocaleDateString('en-CA', { timeZone: RUN_WINDOW_TIME_ZONE });
  return endDay < today ? endMs : undefined;
}

interface PrimaryButtonState {
  label: 'start' | 'schedule' | 'pause';
  disabled: boolean;
  action: 'resume' | 'schedule' | 'pause';
}

// Resolve the start/schedule/pause button for the current status + run window.
function primaryButtonState(
  status: CampaignStatus,
  startMs: number | undefined,
  endMs: number | undefined
): PrimaryButtonState {
  // Terminal status: button is inert.
  if (status === 'completed') {
    return { label: 'pause', disabled: true, action: 'pause' };
  }

  // Running → the same button pauses.
  if (status === 'active' || status === 'scheduled') {
    return { label: 'pause', disabled: false, action: 'pause' };
  }

  // Pending (or test) → start now or schedule for the future. Window rule is
  // the shared one the API walk engine filters on (`isWithinRunWindow`):
  // no start date = in window now, so Start is enabled without a date; an
  // already-ended window (endDate in the past) keeps Start disabled.
  if (startMs != null && startMs > Date.now()) {
    return { label: 'schedule', disabled: false, action: 'schedule' };
  }
  const inWindow = isWithinRunWindow(startMs, endMs, Date.now());
  return { label: 'start', disabled: !inWindow, action: 'resume' };
}

// Sub-labels for statusReason (ADR-010 §3 / A16), shown under the StatusPill
// in the Status section and beside the date range (054-05). Twin of
// STATUS_REASON_LABELS in campaigns.tsx (the list screen's StatusCell) — that
// file is not edited by this epic, so the strings are copied; `schedule` and
// null are absent — the pill already communicates those states.
const STATUS_REASON_LABELS: Partial<Record<CampaignStatusReason, string>> = {
  cap_total: 'Completed — cap hit',
  end_date: 'Completed — end date',
  manual: 'Paused by admin',
};

// ---------------------------------------------------------------------------
// FieldError helper
// ---------------------------------------------------------------------------

function FieldError({ messages }: { messages?: string[] }) {
  if (!messages || messages.length === 0) return null;
  return (
    <p className="mt-1 text-sm text-destructive" role="alert">
      {messages.join(', ')}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Suppression display-name resolution (task 021-02; post-save path — 025-05 #45)
// ---------------------------------------------------------------------------

// Resolve the linked suppression file's display name against the campaign's
// suppression-files list — the same read the edit-load path makes, so the
// post-save refresh resolves identically. Returns null when the read fails or
// the linked id is not in the list; the render turns null into the explicit
// "file #<id> (name unavailable)" fallback (never a bare numeric id).
async function resolveSuppressionFileName(
  campaignId: string,
  suppressionFileId: string
): Promise<string | null> {
  try {
    const files = await api.getSuppressionFiles(campaignId);
    const linked = files.find((file) => file.id === suppressionFileId);
    return linked ? linked.originalFilename : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Partner Send — copy (034-02; epic 034 README §Decisions pre-resolved, ADR-016)
// ---------------------------------------------------------------------------

const PARTNER_SEND_DESC =
  'Choose how leads reach the advertiser. Simple URL — a CRM URL with %email%-style ' +
  'placeholders, GET or POST. Advertiser endpoint — a call defined once on the ' +
  "advertiser's Advertiser endpoints screen; you supply this campaign's values. A " +
  'click-out Custom Opt-in opens its Direct CPC URL as-is.';

/** Switching a campaign ONTO an endpoint (epic README decision 7). */
const SWITCH_ONTO_ENDPOINT_WARNING =
  "On save, this campaign's inline URL, method, credential and response phrases " +
  "are cleared — the endpoint's transport is used instead";

/** Switching a campaign OFF an endpoint (epic README decision 7). */
const SWITCH_OFF_ENDPOINT_WARNING =
  'On save, the endpoint link is removed and the campaign sends to the URL typed here';

/** The no-secrets rule for variable values, ADR-016 §3's wording. */
const ENDPOINT_VALUES_NO_SECRETS_HINT =
  'Values are plain text and are shown to everyone who opens this campaign — never ' +
  'type a token or password here';

/**
 * Summarise an endpoint's response_rules for the read-only view (034-03,
 * Scope 4). Each category shows its phrases or "default" when absent.
 */
function formatEndpointPhrases(rules: CrmResponseRules | undefined): string {
  const fmt = (arr: string[] | undefined): string =>
    arr && arr.length > 0 ? arr.join(', ') : 'default';
  return (
    `accepted: ${fmt(rules?.accepted_contains)}, ` +
    `duplicate: ${fmt(rules?.duplicate_contains)}, ` +
    `rejected: ${fmt(rules?.rejected_contains)}`
  );
}

/**
 * The label the picker shows for the row's own endpoint when it is not among
 * the advertiser's active ones (epic README §Decisions pre-resolved: listed,
 * marked, never auto-cleared — a save keeps the 409 the server answers, which
 * names the endpoint). A row this form has seen carries its name and status; one
 * it has not (deleted, or a list read that failed) can only be named by id.
 */
function endpointOptionLabel(id: string, endpoint: PartnerEndpointResponse | null): string {
  if (!endpoint) return `endpoint #${id} (retired or missing)`;
  return endpoint.status === 'retired' ? `${endpoint.name} (retired)` : endpoint.name;
}

// The offer-page template ids the Custom Opt-in form's Template select
// offers (epic 106). '' = Standard (the default look, sent as an absent key);
// the other entries ride the wire verbatim.
const OFFER_TEMPLATE_OPTIONS = [
  { value: '', label: 'Standard' },
  { value: 'partner-hero-v1', label: 'Partner hero (SPECIAL PARTNER OFFER)' },
] as const;

type OfferTemplateOptionValue = (typeof OFFER_TEMPLATE_OPTIONS)[number]['value'];

/**
 * Narrow a select's change value to the picker's option set (epic 106): the
 * value can only be one of the rendered `<option>` values, so the exhaustive
 * find is the assertion — an unknown value (never producible by this select)
 * falls back to Standard.
 */
function offerTemplateOptionValue(rawValue: string): OfferTemplateOptionValue {
  const match = OFFER_TEMPLATE_OPTIONS.find((option) => option.value === rawValue);
  return match ? match.value : '';
}

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

export function CampaignForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  // True when Duplicate landed here as a fresh clone (task 023-08) — via the
  // legacy history-state channel or the ?freshClone=1 query param (069-03
  // D5); both mean the same thing.
  const [searchParams] = useSearchParams();
  const freshClone =
    readFreshCloneState(useLocation().state) || searchParams.get('freshClone') === '1';
  const { success, error: toastError, info } = useToast();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [sites, setSites] = useState<Array<{ id: string; siteId: string; label: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Epic 095 D6: true while the Image field's upload is in flight — both Save
  // rows and the Enter-key path are gated on it (the payload would carry the
  // half-state: the URL cleared, the minted one not yet landed).
  const [imageUploading, setImageUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  // Inline action feedback (QA 054 run 1, B-02/B-03): the error line for a
  // failed lifecycle POST — the toasts auto-dismiss in 3 s and the QA-run 409
  // body was never read by the admin — and the success line for a save or
  // lifecycle action (the form's save-confirmation channel per the manager
  // ruling: inline, no new dependency). Set beside the existing toasts,
  // cleared on the next action attempt; display only.
  const [actionFeedback, setActionFeedback] = useState<{
    kind: 'error' | 'success';
    message: string;
  } | null>(null);
  const sendUrlRef = useRef<HTMLInputElement>(null);
  const slugHandEdited = useRef<boolean>(false); // Tracks if user has manually edited pageSlug (task 015-09)
  // 054-03: tracks a manual edit of the Disclaimer field, gating the style-aware
  // create-mode prefill (the effect beside the htmlContent prefill below).
  const disclaimerHandEdited = useRef<boolean>(false);
  // 054-04 (Q119): tracks a manual edit of the GA click-event label, gating the
  // slug-follow effect below. Set on every edit-mode load (fresh clone
  // included) so a stored — or duplicated — label is never auto-filled.
  const gaLabelHandEdited = useRef<boolean>(false);
  // 054-08 (Q107): latches any ESP-destinations interaction (Add / Remove /
  // list-id edit) so the create-mode default seed never fights the operator —
  // a removed Maropost TT default stays removed for the form session, even
  // across a style round-trip. The ref is consulted only under !isEdit, so its
  // value across task 01's create → edit transition (no remount) is irrelevant.
  const espTouched = useRef<boolean>(false);
  // Suppression file display name (task 021-02; post-save re-resolution — 025-05
  // #45): the linked file's originalFilename, resolved against the campaign's
  // suppression-files list. Tri-state: undefined = resolution still in flight
  // (the render holds the line rather than flashing a fallback); null =
  // resolution failed or the id is not in the list (the render shows the
  // explicit "file #<id> (name unavailable)" fallback — never a bare numeric
  // id); string = resolved name.
  const [suppressionFileName, setSuppressionFileName] = useState<string | null | undefined>(
    undefined
  );
  // HF-001: the campaign's saved (server) style for edit mode. Null until the
  // campaign loads. Only updated when form state is refreshed from a server
  // response (load or save). Never updated by the style dropdown alone — that
  // is a local edit and must not affect which sections the server owns.
  // Used to gate <CtoLinksSection>: mounting it before the server has
  // persisted cto_email fires a spurious 400 → error toast (QA #57).
  const [savedStyle, setSavedStyle] = useState<CampaignStyle | null>(null);
  // Create-mode CTO link drafts (epic 037-06): the section's client-local rows,
  // bridged up via onDraftsChange, ride the aggregate create payload's `links`
  // array (server-side same-tx attach). Edit mode never reads this — a saved
  // campaign's rows persist through their own endpoints.
  const [ctoLinkDrafts, setCtoLinkDrafts] = useState<LinkDraft[]>([]);
  // Row errors from the save gate, keyed by draft key — pushed down so an
  // invalid draft blocks Save with the failure at the offending row.
  const [ctoLinkRowErrors, setCtoLinkRowErrors] = useState<Record<string, string[]>>({});
  // Partner endpoints for the picker (034-02, ADR-016 §2/§7), advertiser-scoped.
  // The list API returns EVERY row of the advertiser — its query has no status
  // filter (partner-endpoints.service.ts:48–51) — so the picker is what filters
  // to active, plus the row's own endpoint when it is not among them, marked.
  const [endpoints, setEndpoints] = useState<PartnerEndpointResponse[]>([]);
  const [endpointsLoaded, setEndpointsLoaded] = useState(false);
  const [endpointsError, setEndpointsError] = useState<string | null>(null);
  // Every endpoint row this form has seen, by id. The picked one must stay
  // identifiable after an advertiser change drops it from the list — that is the
  // only way "this endpoint belongs to another advertiser" can be said at all,
  // instead of the selection being silently kept (epic README decision 5).
  const seenEndpoints = useRef(new Map<string, PartnerEndpointResponse>());
  // The mode the campaign was LOADED in, so the switch banners compare against
  // the server's row rather than the last keystroke (the HF-001 `savedStyle`
  // precedent). Null in create mode — there is no banner there (scope 3).
  const [loadedSendVia, setLoadedSendVia] = useState<'simple' | 'endpoint' | null>(null);
  // Partner credentials for the credential picker (034-03, ADR-011 / ADR-016 §6),
  // advertiser-scoped, reloaded on every advertiser change.
  const [credentials, setCredentials] = useState<PartnerCredentialRowResponse[]>([]);
  // Advanced disclosure open/closed (034-03, Scope 3): starts collapsed.
  // Reopened on edit load when the row already has auth or any phrase list so
  // a stored credential or phrase is never hidden behind the toggle.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Run a test panel (034-04): the active run id, its polled detail.
  const [formRunId, setFormRunId] = useState<string | null>(null);
  const [formRunDetail, setFormRunDetail] = useState<TestRunDetail | null>(null);

  // Refetch callback for useRunPolling — reads formRunId via closure; the
  // polling hook stores it in a ref so it always calls the latest version.
  const refetchFormRun = useCallback(() => {
    if (!id || !formRunId) return;
    getRun(id, formRunId)
      .then(setFormRunDetail)
      .catch(() => {
        // Transient — next poll retries.
      });
  }, [id, formRunId]);

  const { timedOut: formRunTimedOut, refresh: formRunRefresh } = useRunPolling({
    runId: formRunId,
    status: formRunDetail?.run.status ?? null,
    refetch: refetchFormRun,
  });

  // Load reference data + campaign
  useEffect(() => {
    Promise.all([api.getAdvertisers(), api.getSites()])
      .then(([a, s]) => {
        setAdvertisers(a);
        // Convert SiteResponse to the shape SiteIdPicker expects
        setSites(s.map((site) => ({ id: site.id, siteId: site.siteId, label: site.label })));
      })
      .catch((err: ApiError) => setError(err.message));

    if (isEdit && id) {
      api
        .getCampaign(id)
        .then((c) => {
          const f = formFromCampaign(c);
          // Force limitType to 'unique_leads' for Co-op campaigns (task 015-04):
          // correct legacy data on load so the locked selector shows the right value.
          if (useStyleFlags(f.style).isCoop && f.limitType !== 'unique_leads') {
            f.limitType = 'unique_leads';
          }
          setForm(f);
          setSavedStyle(f.style); // HF-001: pin the server-persisted style
          setLoadedSendVia(f.sendVia ?? 'simple'); // 034-02: pin the server-persisted Send via
          // Expand the Advanced disclosure if the row already has a credential
          // or any response phrase configured (034-03, Scope 3).
          setAdvancedOpen(
            Boolean(f.credentialId) ||
              Boolean(f.acceptedContains) ||
              Boolean(f.duplicateContains) ||
              Boolean(f.rejectedContains)
          );
          // Treat existing slugs as hand-edited to prevent overwriting (task 015-09).
          // Fresh clone (task 023-08): keep the flag false so the first title edit
          // regenerates the slug for the new month instead of keeping "...-copy".
          if (f.pageSlug.trim() && !freshClone) {
            slugHandEdited.current = true;
          }
          // 054-04 (Q119): an edit session never auto-fills the GA label — set
          // unconditionally, fresh clone included, so a duplicate keeps the
          // label copied from its source campaign.
          gaLabelHandEdited.current = true;
          // Resolve the linked suppression file's display name (task 021-02):
          // one extra GET on edit load only. Failure or an id missing from the
          // list leaves null — the render shows the explicit
          // "(name unavailable)" fallback for that case.
          if (f.suppressionFileId) {
            resolveSuppressionFileName(id, f.suppressionFileId).then(setSuppressionFileName);
          } else {
            setSuppressionFileName(null);
          }
        })
        .catch((err: ApiError) => setError(err.message));
    }
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const fieldErrors = (path: string): string[] | undefined => validationErrors[path];

  const flags = useStyleFlags(form.style);
  // HF-110: which section copy applies — own partner send, click-out, or Paid Social.
  const copyMode: 'send' | 'clickOut' | 'paidSocial' = !flags.hasPartnerSend
    ? 'paidSocial'
    : flags.isOptin && form.sendMode === 'click_out'
      ? 'clickOut'
      : 'send';
  // 054-03: the selected advertiser's name, for the Custom Opt-in disclaimer
  // hint — the walk's resolved fallback line names the advertiser
  // (OfferActions.resolveOfferDisclaimer), so the hint shows what "empty"
  // means with the real name; "the advertiser" while none is picked.
  const selectedAdvertiserName = advertisers.find((a) => a.id === form.advertiserId)?.name;

  // Epic 106 (ruling 2026-10-20): the offer-page template picker's state —
  // '' = Standard (the default look), 'partner-hero-v1' = Partner hero
  // (SPECIAL PARTNER OFFER). The template-specific fields activate only
  // while Partner hero is selected.
  const isPartnerHero = flags.isOptin && form.offerTemplate === 'partner-hero-v1';
  // ---- Partner Send: endpoint mode (034-02, ADR-016 §2) --------------------
  // The mode a save will actually apply — `sendVia` is the operator's pick,
  // this is the pick reconciled with the lead-post-only rule.
  const sendVia = effectiveSendVia(form, flags);
  // The two optional FormState fields, read once with their defaults.
  const endpointId = form.endpointId ?? '';
  const endpointValues = form.endpointValues ?? {};
  const pickedEndpoint = endpointId ? (seenEndpoints.current.get(endpointId) ?? null) : null;
  const activeEndpoints = endpoints.filter((row) => row.status === 'active');
  const endpointInActiveList = activeEndpoints.some((row) => row.id === endpointId);
  // An endpoint left over from a previous advertiser is reported, never kept
  // silently (epic README decision 5) — and never auto-cleared.
  const endpointOfOtherAdvertiser =
    pickedEndpoint !== null && pickedEndpoint.advertiserId !== form.advertiserId;
  const endpointVariables = pickedEndpoint ? pickedEndpoint.variables : [];
  // The advertiser has no endpoint to offer and this row points at none: the
  // empty state takes the picker's place.
  const endpointPickerEmpty =
    Boolean(form.advertiserId) &&
    endpointsLoaded &&
    activeEndpoints.length === 0 &&
    endpointId === '';
  // 085 D9: the partner slice this form would save, checked by the server's
  // dry run while it is edited — only for a style with partner send and once
  // an advertiser is picked. A failed check yields null (nothing shown).
  const partnerConfigIssues = usePartnerConfigCheck(
    flags.hasPartnerSend && form.advertiserId
      ? {
          ...(isEdit && id ? { campaignId: id } : {}),
          advertiserId: form.advertiserId,
          campaignStyle: form.campaignStyle,
          crmConfig: crmConfigFromForm(form, flags),
          suppression: {
            enabled: form.suppressionEnabled,
            prepingApiUrl: form.suppressionPrepingUrl.trim(),
          },
        }
      : null,
    checkCampaignPartnerConfig
  );

  const setEndpointValue = (key: string, value: string) =>
    setForm((f) => ({ ...f, endpointValues: { ...(f.endpointValues ?? {}), [key]: value } }));

  // Credential picker computed vars (034-03). Active = not revoked; the stored
  // credential is kept for display even when revoked (marked, never auto-cleared).
  const activeCredentials = credentials.filter((c) => c.revokedAt == null);
  const storedCredentialId = form.credentialId ?? '';
  const storedCredential =
    storedCredentialId !== ''
      ? (credentials.find((c) => c.id === storedCredentialId) ?? null)
      : null;
  const isStoredCredentialRevoked = storedCredential !== null && storedCredential.revokedAt != null;

  // Endpoint list load, and reload on every advertiser change (scope 4). A style
  // with no Partner Send section never reads, and neither does a form with no
  // advertiser picked — the list endpoint REQUIRES advertiserId and would answer
  // 400 (partner-endpoints.controller.ts:59–63), the B-01 failure on the
  // endpoint screen. A failed read is a field-level message, never a thrown render.
  useEffect(() => {
    setEndpointsError(null);
    setEndpointsLoaded(false);
    if (!flags.hasPartnerSend || !form.advertiserId) {
      setEndpoints([]);
      return;
    }
    let cancelled = false;
    getPartnerEndpoints(form.advertiserId)
      .then((rows) => {
        if (cancelled) return;
        for (const row of rows) seenEndpoints.current.set(row.id, row);
        setEndpoints(rows);
        setEndpointsLoaded(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEndpoints([]);
        setEndpointsError(
          err instanceof Error && err.message ? err.message : 'Failed to load advertiser endpoints'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [form.advertiserId, flags.hasPartnerSend]);

  // Partner credentials load, reload on every advertiser change (034-03).
  // Same guard as the endpoint list: no Partner Send section and no advertiserId
  // → no request. Errors are silently discarded — an empty picker is harmless.
  useEffect(() => {
    setCredentials([]);
    if (!flags.hasPartnerSend || !form.advertiserId) return;
    let cancelled = false;
    getPartnerCredentials(form.advertiserId)
      .then((rows) => {
        if (!cancelled) setCredentials(rows);
      })
      .catch(() => {
        if (!cancelled) setCredentials([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.advertiserId, flags.hasPartnerSend]);

  // Force limitType to 'unique_leads' for Co-op campaigns (task 015-04).
  // The save path forces it regardless, but this keeps the disabled selector's
  // displayed value correct on the new-campaign path and after a style switch.
  // Deps are intentionally limited to form.style so this only re-runs when the
  // style changes, not on every keystroke that touches form.limitType.
  useEffect(() => {
    if (flags.isCoop && form.limitType !== 'unique_leads') {
      set('limitType', 'unique_leads');
    }
  }, [form.style]);

  // Default ClickMagick domain when CM is enabled (task 015-06).
  // When an operator enables ClickMagick for a Custom Opt-in campaign, the
  // tracking domain should pre-fill with track.tradingtips.com rather than
  // requiring manual entry. The default applies only when the domain field is
  // empty — a loaded campaign with an existing domain keeps its value; a
  // hand-edited domain is never overwritten.
  useEffect(() => {
    if (form.cmEnabled === true && form.cmDomain.trim() === '') {
      set('cmDomain', 'track.tradingtips.com');
    }
  }, [form.cmEnabled]);

  // B4: Unconditional captureStrategy derivation from style (operator decision, round-3 follow-up).
  // The Capture strategy section is now hidden for all styles; captureStrategy is derived from style:
  // - paid_social → 'email_form'
  // - cto_email → 'parameter_capture'
  // - external_form → 'email_form' (epic 096 D1a — the third email-form journey)
  // - co_op/custom_optin → not sent at all (field omitted from payload)
  // This effect runs unconditionally on style change, overwriting any existing value.
  useEffect(() => {
    if (flags.isPaidSocial || flags.isExternalForm) {
      set('captureStrategy', 'email_form');
    } else if (flags.isCtoEmail) {
      set('captureStrategy', 'parameter_capture');
    } else {
      // Non-journey styles: clear the field so it's omitted from payload
      set('captureStrategy', '');
    }
  }, [form.style]);

  // Epic 022 task 02: a NEW journey-style campaign starts with both eligibility
  // steps pre-added in execution order (1 cloudflare, 2 email_oversight), each
  // carrying the operator-specified policies as explicit config data (see
  // ELIGIBILITY_STEP_DEFAULT_CONFIG for why the values must be stored, not
  // implied). Paid Social + External Form since epic 024 (task 04) / 096
  // (task 03 — the /ext interstitial waits on the cloudflare step's Turnstile
  // token); CTO Email has no per-campaign eligibility section — its
  // validations live on the link rows. Deps are intentionally limited to
  // form.style so this fires only when the form enters the style with an
  // empty section: a user who deletes a step keeps it deleted for the rest of
  // the form session, and edit-mode hydration never injects defaults into a
  // stored campaign.
  useEffect(() => {
    if (
      isEdit ||
      (!flags.isPaidSocial && !flags.isExternalForm) ||
      form.eligibilitySteps.length > 0
    ) {
      return;
    }
    setForm((f) => {
      // Re-check against the freshest state: the section must only be seeded
      // while it is genuinely empty (also makes a StrictMode double-run a no-op).
      if (
        (!isPaidSocialStyle(f.style) && !isExternalFormStyle(f.style)) ||
        f.eligibilitySteps.length > 0
      ) {
        return f;
      }
      return {
        ...f,
        eligibilitySteps: [
          {
            id: newRowId(),
            stepKind: 'cloudflare',
            configJson: ELIGIBILITY_STEP_DEFAULT_CONFIG.cloudflare,
          },
          {
            id: newRowId(),
            stepKind: 'email_oversight',
            configJson: ELIGIBILITY_STEP_DEFAULT_CONFIG.email_oversight,
          },
        ],
      };
    });
  }, [form.style]);

  // Q107 (054-08, FR-B23), widened by HF-085: a NEW journey campaign starts
  // with all four ESP_DEFAULT_DESTINATIONS rows (Maropost TT, Iterable TWM,
  // Iterable SIS, Beehiiv TSI — the catalog lives in
  // components/esp-destinations-section.tsx since epic 101 fix 06) — editable
  // and removable. Same rules as
  // the eligibility-steps seed above: create mode
  // only, never on edit hydration, seeding only while the section is genuinely
  // empty. On top of that precedent, the espTouched latch means a removed
  // default stays removed for the whole form session — the eligibility seed
  // would re-seed after a style round-trip, this one must not. The ref is
  // consulted only under !isEdit, so its value across task 01's create → edit
  // transition (no remount) is irrelevant (epic README §Decisions).
  useEffect(() => {
    if (isEdit || !flags.isJourney || form.espDestinations.length > 0 || espTouched.current) {
      return;
    }
    setForm((f) => {
      // Re-check against the freshest state: the section must only be seeded
      // while it is genuinely empty and untouched (also makes a StrictMode
      // double-run a no-op).
      if (!isJourneyStyle(f.style) || f.espDestinations.length > 0 || espTouched.current) {
        return f;
      }
      const defaults = isPaidSocialStyle(f.style)
        ? ESP_DEFAULT_DESTINATIONS.paid_social
        : ESP_DEFAULT_DESTINATIONS.cto_email;
      return {
        ...f,
        espDestinations: [
          ...f.espDestinations,
          ...defaults.map((d) => ({
            id: newRowId(),
            provider: d.provider,
            brandList: d.brandList,
            listIdentifier: d.listIdentifier,
            fieldMappingsJson: '{}',
            transactionalCampaignId: '',
            transactionalOn: '' as const,
            // HF-084: the journey UI is hidden and no longer prefilled —
            // Manny ruled the journey out (Q124). A seeded TT row starts empty.
            maropostJourneyId: '',
            maropostTriggerUid: '',
            maropostTags: '',
          })),
        ],
      };
    });
  }, [form.style]);

  // Template-picker default prefill (task 019-02 item 15, binding rule): CREATE
  // mode only, and only while the field is empty. Edit-mode hydration never
  // replaces a stored value — not even an empty-stored one. Deps are limited to
  // mode + section visibility so this fires once per mount and again only when
  // the Offer page section appears via a style change; an operator clearing the
  // field to write custom HTML is never fought with a re-fill.
  useEffect(() => {
    if (!isEdit && flags.hasOfferPage && form.htmlContent === '') {
      set('htmlContent', DEFAULT_PAGE_HTML);
    }
  }, [isEdit, flags.hasOfferPage]);

  // Style-aware disclaimer prefill (054-03): in create mode a new Custom Opt-in
  // campaign starts EMPTY — its offer step then renders the walk's resolved
  // advertiser line (OfferActions.resolveOfferDisclaimer) instead of the Paid
  // Social text this form used to store silently — while every other style
  // keeps today's built-in TradingTips default (Paid Social is the only other
  // style that shows the field; Co-op / CTO Email never do). Create only, and
  // only while the field has not been hand-edited, mirroring the htmlContent
  // prefill above: edit hydration never replaces a stored value, and an
  // operator's typed text is never fought with a re-fill. The ref is consulted
  // only under !isEdit, so its value across task 01's create → edit transition
  // (no remount) is irrelevant.
  useEffect(() => {
    if (!isEdit && !disclaimerHandEdited.current) {
      set('optInDisclaimer', flags.isOptin ? '' : OPT_IN_DISCLAIMER_DEFAULT);
    }
  }, [isEdit, flags.isOptin]);

  // CTO Email auto-slug from name (045-03): the Offer page section is hidden for
  // cto_email so pageSlug cannot derive from pageTitle. In create mode, derive
  // from the campaign name with the same slugify + hand-edit guard used in the
  // pageTitle onChange handler. The slug is never shown (field is hidden) but is
  // still sent in the payload so the landing-data feed keeps working (024 #4).
  useEffect(() => {
    if (!isEdit && flags.isCtoEmail && (!slugHandEdited.current || !form.pageSlug.trim())) {
      set('pageSlug', slugify(form.name));
    }
  }, [form.name, isEdit, flags.isCtoEmail]);

  // GA click-event label follows the page slug in create mode (Q119 (b), epic
  // 054-04): for every hosted-page style the label tracks pageSlug — derived
  // from the page title in the Offer page onChange, or from the campaign name
  // for CTO Email above — until the operator edits the label by hand. Latch
  // only: a cleared label stays cleared (blank is a meaningful value — it
  // disables GA click tracking for the campaign), unlike the slug rule's
  // resume-on-empty. The ref is consulted only under !isEdit, so its value
  // across task 01's create → edit transition (no remount) is irrelevant.
  useEffect(() => {
    if (!isEdit && flags.hasHostedPage && !gaLabelHandEdited.current) {
      set('gaEventLabel', form.pageSlug);
    }
  }, [form.pageSlug, isEdit, flags.hasHostedPage]);

  // Eligibility steps management
  // Manual adds pre-fill the same default config the create-mode seeding writes
  // (epic 022 task 02); internal_duplicate keeps the empty config it always had.
  const addEligibilityStep = (stepKind: EligibilityStepKind) =>
    setForm((f) => {
      // Prevent duplicate steps of the same kind
      if (f.eligibilitySteps.some((s) => s.stepKind === stepKind)) {
        return f;
      }
      return {
        ...f,
        eligibilitySteps: [
          ...f.eligibilitySteps,
          {
            id: newRowId(),
            stepKind,
            configJson:
              stepKind === 'cloudflare' || stepKind === 'email_oversight'
                ? ELIGIBILITY_STEP_DEFAULT_CONFIG[stepKind]
                : '{}',
          },
        ],
      };
    });

  // #42 variant B: card order is the ordering control. Adjacent swap keeps the
  // array the single source of order; ord is derived from the index at save time.
  const moveEligibilityStep = (rowId: string, dir: 'up' | 'down') =>
    setForm((f) => {
      const index = f.eligibilitySteps.findIndex((r) => r.id === rowId);
      const target = dir === 'up' ? index - 1 : index + 1;
      const moved = f.eligibilitySteps[index];
      const adjacent = f.eligibilitySteps[target];
      if (!moved || !adjacent) return f;
      const steps = [...f.eligibilitySteps];
      steps[index] = adjacent;
      steps[target] = moved;
      return { ...f, eligibilitySteps: steps };
    });

  const removeEligibilityStep = (rowId: string) =>
    setForm((f) => ({
      ...f,
      eligibilitySteps: f.eligibilitySteps.filter((r) => r.id !== rowId),
    }));

  const updateEligibilityStep = (rowId: string, patch: Partial<EligibilityStepRow>) =>
    setForm((f) => ({
      ...f,
      eligibilitySteps: f.eligibilitySteps.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    }));

  // Site ID management
  const toggleRestrictedSite = (siteId: string, checked: boolean) =>
    setForm((f) => ({
      ...f,
      restrictedSiteIds: checked
        ? [...f.restrictedSiteIds, siteId]
        : f.restrictedSiteIds.filter((s) => s !== siteId),
    }));

  // Suppression file selection (browse OR drop — task 023-02). The pending file
  // in form state is the single source of truth (a drop cannot populate the
  // input's read-only FileList). The linked file's display state (id + resolved
  // name) is untouched: a pending selection does not change the stored link, so
  // the Linked-file line keeps rendering beside the pending chip and survives
  // clearing it (QA #45). A successful save re-pins both from the post-save
  // state (the upload response, or the post-save list re-resolution).
  const selectSuppressionFile = (file: File | null) => {
    set('suppressionPendingFile', file);
  };

  // Suppression upload folded into the save (task 025-04, #44): a file sitting
  // in the drop zone is committed by Create/Update, not by a separate button.
  // The upload endpoint is per-campaign and links the file server-side, so
  // "upload → link → save" is this one call before the save. HF-095: a failed
  // upload no longer aborts the save silently — the caller surfaces the
  // reason in the outcome toast, so this helper reports the error back
  // instead of toasting it itself (one toast per failure, not two).
  const uploadPendingSuppression = async (
    campaignId: string
  ): Promise<{ file: SuppressionFileResponse } | { error: string } | null> => {
    const file = form.suppressionPendingFile;
    if (!file) return null;
    try {
      const uploaded = await api.uploadSuppressionFile(campaignId, file);
      setSuppressionFileName(uploaded.originalFilename);
      // #49: a successful upload mints a new, readable file — the unreadable-file
      // warning cannot survive it (the flag refreshes from the detail read on
      // the next load anyway).
      return { file: uploaded };
    } catch (err) {
      const apiError = err as ApiError;
      return { error: apiError.message || 'Failed to upload suppression file' };
    }
  };

  // Lifecycle actions
  //
  // onSuccess is an optional post-refresh callback used by the activate branch
  // to inspect activationWarnings (034-04). Existing callers (pause, schedule,
  // complete) omit it — their call sites are unchanged. The #49 suppression
  // signal and the success toast both fire before onSuccess.
  const runLifecycle = async (
    fn: () => Promise<CampaignResponse>,
    label: string,
    onSuccess?: (result: CampaignResponse) => void
  ) => {
    // B-02/B-03: a new action attempt clears the previous feedback line.
    setActionFeedback(null);
    try {
      const updated = await fn();
      // #49: lifecycle responses carry no suppression-file status (it rides the
      // campaign read only) — keep the loaded signal rather than flashing the
      // unreadable-file warning off on every pause/resume.
      setForm({
        ...formFromCampaign(updated),
        suppressionFileUnreadable: form.suppressionFileUnreadable,
      });
      success(`Campaign ${label}`);
      // B-03: the inline success line (e.g. "Scheduled" / "Paused") — the toast
      // alone auto-dismisses before the admin can read it.
      setActionFeedback({ kind: 'success', message: `${label[0].toUpperCase()}${label.slice(1)}` });
      onSuccess?.(updated);
    } catch (err) {
      const apiError = err as ApiError;
      // B-02: the api error body stays visible as the inline line — the toast
      // auto-dismisses in 3 s, which is how the QA-run 409 went unread.
      const message = apiError.message || `Failed to ${label} campaign`;
      toastError(message);
      setActionFeedback({ kind: 'error', message });
    }
  };

  // Form submission (054-01): `intent` decides only the post-success
  // navigation — 'stay' keeps the operator on the form (a create lands on the
  // new campaign's edit screen, an update refreshes in place), 'close' returns
  // to the campaigns list. HF-094: 'reopen' is update-only — after the save
  // lands it calls reopenCampaign in its own try/catch (QA-17), then stays on
  // the form like 'stay' (no navigation) whether the reopen lands or is
  // refused. Validation, payload building and error handling are identical
  // for all three. 069-03 (D4): `opts.scheduleOnCreate` is create-mode only —
  // the Save & schedule action posts the row directly as 'scheduled'.
  const submit = async (
    intent: 'stay' | 'close' | 'reopen',
    opts: { scheduleOnCreate?: boolean } = {}
  ) => {
    setSubmitting(true);
    setValidationErrors({});
    // B-02/B-03: a new action attempt clears the previous feedback line.
    setActionFeedback(null);

    // Client-side validation for required fields (#7, #8, #9)
    const clientErrors: Record<string, string[]> = {};
    const styleFlags = useStyleFlags(form.style);

    // Campaign name is always required
    if (!form.name.trim()) {
      clientErrors.name = ['Campaign name is required'];
    }

    // Custom Opt-in: page title (pageSlug) is required
    if (styleFlags.isOptin && !form.pageSlug.trim()) {
      clientErrors.pageSlug = ['Custom opt-in page slug is required'];
    }

    // Custom Opt-in: CPC (pricePerLead) is required
    if (styleFlags.isOptin && !form.pricePerLead.trim()) {
      clientErrors.pricePerLead = ['Offer ads cost (CPC) is required'];
    }

    // Co-op: Cost per lead (pricePerLead) is required
    if (styleFlags.isCoop && !form.pricePerLead.trim()) {
      clientErrors.pricePerLead = ['Cost per lead is required'];
    }

    // 066 D4: Priority is optional, but a value must be a whole number >= 1 —
    // it is a pin position ahead of the revenue order, so 0, negatives and
    // fractions are refused client-side (the wire would reject them too).
    if (styleFlags.isCoop && (form.priority ?? '').trim()) {
      const p = Number(form.priority);
      if (!Number.isInteger(p) || p < 1) {
        clientErrors.priority = ['Priority must be a whole number of 1 or more'];
      }
    }

    // Epic 091 D9: the Facebook share is optional, but a value must be a whole
    // number 0-100, and 1-100 needs a Daily lead cap to take a share of (D6) —
    // 0 blocks Facebook leads and needs no cap.
    if (styleFlags.isCoop && (form.maxFacebookSharePct ?? '').trim()) {
      const share = Number(form.maxFacebookSharePct);
      if (!Number.isInteger(share) || share < 0 || share > 100) {
        clientErrors.maxFacebookSharePct = [
          'Max Facebook share must be a whole number from 0 to 100, or empty',
        ];
      } else if (share >= 1 && !form.dailyCap.trim()) {
        clientErrors.maxFacebookSharePct = [
          'Set a Daily lead cap to use a share, or enter 0 to block Facebook leads',
        ];
      }
    }

    // Epic 096 task 03 (D4): errorUrl is optional but, when typed, must be a
    // URL — the same shape the wire's z.string().url() enforces; blocking here
    // (AC 4) means no API call on a bad value. The Site field is a select of
    // the catalog, so an unknown siteId cannot be typed in the UI.
    if (styleFlags.isExternalForm && (form.errorUrl ?? '').trim()) {
      try {
        new URL((form.errorUrl ?? '').trim());
      } catch {
        clientErrors.errorUrl = [
          'Error/fallback URL must be a valid URL (e.g. https://…), or empty',
        ];
      }
    }

    // Epic 109 task 01: the FB campaign ID is optional but, when typed, must
    // be digits only — the same shape the wire's digit regex enforces;
    // blocking here (AC 4) means no API call on a bad value.
    if (styleFlags.isExternalForm && (form.fbCampaignId ?? '').trim()) {
      if (!/^\d+$/.test((form.fbCampaignId ?? '').trim())) {
        clientErrors.fbCampaignId = [
          'Facebook campaign ID must be digits only (e.g. 7000512942804), or empty',
        ];
      }
    }

    // Partner endpoint mode (034-02): the picker carries Simple URL's required
    // mark in this mode (scope 7), and the two checks the form can decide
    // locally run over the endpoint's declared variables (ADR-016 §3). Token
    // spellings and every other rule stay the server's, answered as a 400.
    if (sendVia === 'endpoint') {
      if (!endpointId) {
        clientErrors['crmConfig.endpoint_id'] = ['Pick an advertiser endpoint'];
      } else if (endpointOfOtherAdvertiser) {
        clientErrors['crmConfig.endpoint_id'] = ['This endpoint belongs to another advertiser'];
      }
      for (const variable of endpointVariables) {
        const value = endpointValues[variable.key] ?? '';
        // A required variable WITH a default is satisfied by it — the blank
        // field means "use the default", exactly as the server reads an absent
        // key (validateEndpointValues).
        if (variable.required && variable.default === undefined && !value.trim()) {
          clientErrors[`crmConfig.values.${variable.key}`] = ['Required'];
        } else if (value.includes('{{')) {
          clientErrors[`crmConfig.values.${variable.key}`] = [
            'Values are literal text — {{…}} is not allowed',
          ];
        }
      }
    }

    const rangeStartMs = DATE_TO_MS(form.startDate);
    const rangeEndMs = DATE_TO_MS(form.endDate);
    // 054-06 (her item 8): Co-op and Custom Opt-in require BOTH bounds — the
    // save is blocked here, before any request, on create and update alike. A
    // duplicated campaign (clone() nulls both dates) therefore shows the
    // required marker and cannot be updated until a range is picked. Paid
    // Social and CTO Email keep optional dates.
    if ((styleFlags.isCoop || styleFlags.isOptin) && (rangeStartMs == null || rangeEndMs == null)) {
      clientErrors.dateRange = ['Date range is required for Co-op and Custom Opt-in campaigns'];
    }
    // Bug #47: end < start is never a legal run window. The calendar cannot
    // build such a pair, but a stored inverted range (loaded on Edit) or any
    // future state-producing path must not be savable either — the walk drops
    // the campaign when `now >= start AND now <= end` can never hold.
    // HF-076: CTO Email doesn't render the Date range field at all (capture is
    // link-gated, not campaign-dated — see the form's Date range gate below),
    // so a stored inverted range on a CTO campaign must not block Save.
    if (
      !styleFlags.isCtoEmail &&
      rangeStartMs != null &&
      rangeEndMs != null &&
      rangeEndMs < rangeStartMs
    ) {
      clientErrors.dateRange = ['End date must be on or after the start date'];
    }

    // CTO link drafts gate (create mode only, epic 037-06): every draft row
    // must parse against the shared createCampaignLinkSchema (the same
    // validator the row's own Save uses) before the campaign POST is allowed.
    // An invalid row blocks Save — no POST — and the failure surfaces at the
    // offending row via ctoLinkRowErrors.
    if (!isEdit && styleFlags.isCtoEmail && ctoLinkDrafts.length > 0) {
      const rowErrors: Record<string, string[]> = {};
      for (const draft of ctoLinkDrafts) {
        const messages = draftFieldErrors(draft);
        if (messages.length > 0) rowErrors[draft.key] = messages;
      }
      setCtoLinkRowErrors(rowErrors);
      if (Object.keys(rowErrors).length > 0) {
        setValidationErrors(clientErrors);
        setSubmitting(false);
        toastError('Fix the highlighted CTO link rows before saving');
        return;
      }
    }

    if (Object.keys(clientErrors).length > 0) {
      setValidationErrors(clientErrors);
      setSubmitting(false);
      toastError('Please fix the validation errors');
      return;
    }

    // 085 D9 Save gate: the latest check holds a blocker this save would
    // create (not a grandfathered one) — stop before any request. The server's
    // 400 stays the authority for a check still in flight.
    if (hasNewBlocker(partnerConfigIssues)) {
      setSubmitting(false);
      toastError('Fix the partner setup problem in Advertiser Send before saving');
      return;
    }

    try {
      const payload = aggregateFromForm(form);
      if (isEdit && id) {
        // Custom Opt-in placement is server-owned (epic 083 D6): an absent key
        // leaves the stored placement unchanged; the form never sends one.
        const updatePayload: CampaignAggregateUpdateInput = { ...payload };
        // Bug #47: "empty = no date" must be written on update. The create
        // payload omits an empty bound (a new row has nothing to clear); on
        // update an absent key means "leave unchanged" server-side, which
        // resurrected the stored dates after Clear and left a stale end behind
        // a lone new start (report steps 1–3, 5). The update wire accepts null
        // for both bounds, and null is what removes the stored date.
        if (updatePayload.startDate === undefined) updatePayload.startDate = null;
        if (updatePayload.endDate === undefined) updatePayload.endDate = null;
        // 066 D4a: a Co-op edit whose Priority was cleared sends an explicit
        // null so the stored pin is removed — the same "empty = clear" write
        // the dates make (Bug #47); an absent key would mean "leave unchanged"
        // and the pin would survive. Create omits the key instead (the create
        // wire rejects null), and non-Co-op styles never carry the field.
        if (styleFlags.isCoop && updatePayload.priority === undefined) {
          updatePayload.priority = null;
        }
        // 090: "Empty = unlimited" (the Daily cap and Limit hints) must be written on
        // update — the explicit-null clear Priority (066 D4a) and the dates (Bug #47)
        // already make; an absent key means "leave unchanged", so a removed cap
        // survived (Chelsie 2026-10-04). Only a BLANK field clears: a value that does
        // not parse (e.g. "1,200") is still left out, so a typo never removes a cap.
        // Not style-gated: hidden fields (CTO Email) round-trip their stored value.
        // Create omits the keys instead (the create wire rejects null).
        if (!form.dailyCap.trim()) updatePayload.dailyCap = null;
        if (!form.limitValue.trim()) updatePayload.limitValue = null;
        // Epic 101 fix 06 (D7): a style without the ESP destinations section
        // (Co-op since fix 06, Custom Opt-in as ever) omits the key entirely
        // on update — absent means "leave unchanged", so the api's reconcile
        // never fires and any out-of-band saved rows persist untouched (R2).
        if (!styleFlags.hasEspDestinations) {
          delete updatePayload.espDestinations;
        }
        // A12 (034-05): every ESP destination row on update carries the transactional
        // pair with explicit null so a previously SQL-set value can be cleared on any
        // row (reconcileEspDestinations distinguishes undefined from null — undefined
        // carries over stored value, null clears it). Non-Maropost rows hold '' in
        // form state, which maps to null here — correct and defensive (Context 5).
        if (styleFlags.hasEspDestinations && updatePayload.espDestinations) {
          updatePayload.espDestinations = updatePayload.espDestinations.map((row, i) => {
            const src = form.espDestinations[i];
            const on = src?.transactionalOn;
            return {
              ...row,
              transactionalCampaignId: src?.transactionalCampaignId?.trim() || null,
              transactionalOn: on === 'success' || on === 'valid' ? on : null,
            };
          });
        }
        // Task 025-04 (#44), amended HF-095: a file in the drop zone is uploaded
        // as part of the save — upload (which links server-side) BEFORE the
        // update. A failed upload no longer aborts the save (the client's other
        // edits still go through); the reason is surfaced in the outcome toast
        // below and the pending file stays in the drop zone for a retry.
        let linkedSuppressionFile: SuppressionFileResponse | null = null;
        let suppressionUploadError: string | null = null;
        if (form.suppressionPendingFile) {
          const uploadResult = await uploadPendingSuppression(id);
          if (uploadResult && 'error' in uploadResult) {
            suppressionUploadError = uploadResult.error;
          } else if (uploadResult) {
            linkedSuppressionFile = uploadResult.file;
          }
        }
        // HF-095: a failed upload must not navigate away on Save & close — she
        // needs to see the retry toast and the drop zone. Demoted here (not at
        // the 054-01 navigation line below, which stays byte-identical so it
        // doesn't collide with HF-094's reopen block inserted at that spot);
        // only 'close' is demoted, so 'stay' is unaffected either way.
        if (suppressionUploadError && intent === 'close') intent = 'stay';
        const updated = await api.updateCampaign(id, updatePayload);
        // HF-095: exactly one toast reports the outcome — the success toast, or
        // (when the upload failed) the reason why the file wasn't attached.
        // uploadPendingSuppression no longer toasts on its own failure.
        if (suppressionUploadError) {
          // HF-095: strip one trailing period from the upstream reason so it
          // doesn't collide with this sentence's own closing punctuation
          // (api.ts's messages are kept verbatim; the join is fixed here).
          toastError(
            `Campaign saved, but the suppression file was not attached: ${suppressionUploadError.replace(/\.$/, '')}. Click Update to try again.`
          );
        } else {
          success('Campaign updated');
        }
        // B-03: the inline save confirmation (the manager ruling: an inline
        // line, not a toast system).
        setActionFeedback({ kind: 'success', message: 'Saved' });
        // A saved slug is committed data (task 023-08): post-save title edits stop
        // regenerating, matching the new-campaign→edit-screen load path. Only the
        // fresh-clone origin is affected — a normal edit already has the flag set.
        slugHandEdited.current = true;
        const refreshed = formFromCampaign(updated);
        if (linkedSuppressionFile) {
          // The upload linked the fresh file id server-side; a bare update
          // response may not echo the suppression block back, so pin it.
          refreshed.suppressionFileId = linkedSuppressionFile.id;
          // #49: the save-path upload minted a readable file — the warning clears.
          refreshed.suppressionFileUnreadable = false;
        } else {
          // #49: the update response carries no suppression-file status — keep
          // the loaded signal so an unrelated save cannot clear the warning.
          refreshed.suppressionFileUnreadable = form.suppressionFileUnreadable;
        }
        if (suppressionUploadError) {
          // HF-095: keep the pending file in the drop zone so Update can retry
          // the upload without re-selecting the file.
          refreshed.suppressionPendingFile = form.suppressionPendingFile;
        }
        setForm(refreshed);
        setSavedStyle(refreshed.style); // HF-001: reflect saved style after update
        setLoadedSendVia(refreshed.sendVia ?? 'simple'); // 034-02: the switch is spent — no banner
        // #45: the update response identifies the linked file by numeric id
        // only (the #28 symptom on the update path) and the display name held
        // in state can be stale or absent — re-resolve it from post-save state
        // exactly like the edit-load path. A save-path upload already pinned
        // the fresh name from the upload response, so only an upload-free save
        // refetches the list.
        if (!linkedSuppressionFile && refreshed.suppressionFileId) {
          resolveSuppressionFileName(id, refreshed.suppressionFileId).then(setSuppressionFileName);
        }
        // HF-094 (QA-17): 'reopen' calls reopenCampaign now that the save
        // above has committed. Its own try/catch keeps a refusal from ever
        // reading as a save failure — the save already succeeded, so the
        // toast/inline feedback above stands and this only adds a second
        // outcome. On success the form state is replaced with the reopened
        // campaign (formFromCampaign(result)), carrying over the client-only
        // fields the save just resolved (suppressionFileUnreadable, the
        // suppressionFileId pin, and — HF-096 — the pending file so a failed
        // upload's retry survives the reopen) — otherwise the next Update
        // would echo the stale `status: 'completed'` (payload builder at
        // :1128) and silently re-complete it. On refusal the save stays
        // committed: the server's message (the 409 cases in
        // campaign.service.ts, e.g. "Raise the lead limit above N before
        // reopening") surfaces via toastError + inline error feedback, no
        // navigation, status stays completed.
        if (intent === 'reopen') {
          try {
            const reopened = await api.reopenCampaign(id);
            const reopenedForm = formFromCampaign(reopened);
            reopenedForm.suppressionFileUnreadable = refreshed.suppressionFileUnreadable;
            reopenedForm.suppressionFileId = refreshed.suppressionFileId;
            reopenedForm.suppressionPendingFile = refreshed.suppressionPendingFile;
            setForm(reopenedForm);
            success('Campaign saved and reopened');
            setActionFeedback({ kind: 'success', message: 'Saved and reopened' });
          } catch (err) {
            const apiError = err as ApiError;
            const message = apiError.message || 'Reopen failed';
            toastError(message);
            setActionFeedback({ kind: 'error', message });
          }
        }
        // 054-01: Save & close returns to the list once the in-place refresh
        // has landed; 'stay' and 'reopen' keep today's stay-on-page behaviour
        // (no navigation either way — HF-094's reopen success/refusal both
        // stay on the form).
        if (intent === 'close') navigate('/campaigns');
      } else {
        // Create-mode CTO links (epic 037-06): the draft rows ride the
        // aggregate create payload — one round trip, attached server-side in
        // the same transaction as the parent. They never join the update
        // payload above (a saved campaign's rows go through their own
        // endpoints), so `links` is added to the create copy only.
        const createPayload: CampaignAggregateCreateInput = { ...payload };
        // 069-03 (D4): Save & schedule creates the row directly in 'scheduled'
        // — the start date already rides the aggregate payload
        // (aggregateFromForm). The server's Item-13 gate 409s a
        // create-as-scheduled without the Partner Send delivery-target link;
        // that failure surfaces inline, not as a raw toast (the catch below).
        if (opts.scheduleOnCreate) createPayload.status = 'scheduled';
        if (styleFlags.isCtoEmail && ctoLinkDrafts.length > 0) {
          createPayload.links = ctoLinkDrafts.map((draft) => payloadFor(draft, Number(draft.rate)));
        }
        // A12 (034-05): for Maropost TT rows include transactional keys only when
        // set — the create schema rejects null; a fresh row has nothing to clear
        // (r1 F4). Non-Maropost rows are passed through unchanged.
        if (styleFlags.hasEspDestinations && createPayload.espDestinations) {
          createPayload.espDestinations = createPayload.espDestinations.map((row, i) => {
            const src = form.espDestinations[i];
            const isMaropostTT = src?.provider === 'maropost' && src?.brandList === 'tt';
            if (!isMaropostTT) return row;
            const id = src?.transactionalCampaignId?.trim();
            const on = src?.transactionalOn;
            return {
              ...row,
              ...(id ? { transactionalCampaignId: id } : {}),
              ...(on === 'success' || on === 'valid' ? { transactionalOn: on } : {}),
            };
          });
        }
        const created = await api.createCampaign(createPayload);

        // 054-01: the screen after a create is now the edit screen, so every
        // style is told the save succeeded and editing continues right here.
        // Styles carrying the Advertiser Send section also point at the
        // Run-a-test block that becomes reachable after the transition.
        const createdToast = flags.hasPartnerSend
          ? 'Campaign created — you are now editing it. Run a test below when you are ready.'
          : 'Campaign created — you are now editing it.';

        // Task 025-04 (#44), amended HF-095: the drop-zone file is uploaded as
        // part of the save. The endpoint is per-campaign, so the row must exist
        // first: create → upload → (linked). A failed upload no longer aborts
        // the save — the row already exists, so the reason is surfaced in the
        // outcome toast and she is sent to the created campaign's edit page to
        // retry the attach with Update (uploadPendingSuppression no longer
        // toasts its own failure — exactly one toast fires here).
        let suppressionUploadError: string | null = null;
        if (form.suppressionPendingFile) {
          const uploadResult = await uploadPendingSuppression(created.id);
          if (uploadResult && 'error' in uploadResult) {
            suppressionUploadError = uploadResult.error;
          } else {
            // 054-01 (Scope 6): the file is committed — clear it from state before
            // navigating. The transition to /campaigns/:id re-runs the load effect
            // without a remount, so this state survives until the re-read lands;
            // an Update clicked inside that window must not upload the file again.
            set('suppressionPendingFile', null);
          }
        }
        if (suppressionUploadError) {
          // HF-095: same trailing-period fix as the update path above.
          toastError(
            `Campaign created, but the suppression file was not attached: ${suppressionUploadError.replace(/\.$/, '')}. Add it again and click Update.`
          );
        } else if (form.suppressionPendingFile) {
          success('Campaign created with suppression file — you are now editing it.');
        } else {
          success(createdToast);
        }
        // B-03: the inline save confirmation, shared by both create branches.
        setActionFeedback({ kind: 'success', message: 'Saved' });

        // 054-01: 'stay' lands on the new campaign's edit screen — the same
        // navigation shape Duplicate uses — where the Run-a-test block (034-04)
        // renders for Co-op and Custom Opt-in. 'close' keeps the old return to
        // the list. No nav state: the toast survives it (ToastProvider wraps the
        // router), and the load effect refetches from the id alone. HF-095: a
        // failed suppression upload always opens the edit screen (the row is
        // already created) regardless of intent, so the retry toast and drop
        // zone are visible.
        if (intent === 'close' && !suppressionUploadError) {
          navigate('/campaigns');
        } else {
          navigate(`/campaigns/${created.id}`);
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (opts.scheduleOnCreate && err.status === 409) {
          // 069-03 (D4): the Item-13 gate's named 409 on a create-as-scheduled
          // (no Partner Send delivery-target link). The generic handler's
          // `_form` slot renders only inside the edit-only Status section, so
          // the reason goes on the inline action line instead — readable, and
          // no raw toast (the toast auto-dismisses before she can read it).
          setActionFeedback({ kind: 'error', message: err.message || 'Schedule failed' });
        } else {
          // Per-field errors when the server sends them; otherwise the message
          // itself is the form-level error. ADR-016 §2's `values` 400 is a plain
          // BadRequestException naming the offending variable — with no `errors`
          // array to map, `_form` is the contract (epic README decision 6), and a
          // toast alone would let the operator lose the reason on the next click.
          setValidationErrors(
            err.validationErrors ?? { _form: [err.message || 'Validation failed'] }
          );
          toastError(err.message || 'Validation failed');
        }
      } else {
        const e = err as Error & { field?: string };
        if (e.field) {
          setValidationErrors({ [e.field]: [e.message] });
        }
        toastError(e.message || 'Failed to save campaign');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // The form's own submission paths — the <form> onSubmit (Enter key included)
  // and the primary Create/Update button — always submit with 'stay' intent
  // (054-01). Only the secondary Save & close buttons pass 'close'; create
  // mode's Save & schedule passes 'stay' + scheduleOnCreate (069-03 D4).
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Epic 095 D6: an upload in flight blocks the save (the disabled buttons
    // gate the click paths; this gates the implicit Enter-key submission the
    // <form> onSubmit also serves).
    if (imageUploading) return;
    void submit('stay');
  };

  // Preview (epic 037 task 05): assemble the UNSAVED form state as a full
  // CampaignResponse, gate it on the shared schema, POST it for a short-lived
  // token and open the landing /preview route in a new tab. Failure paths stay
  // in this tab: a schema-gate miss or a POST error is an error toast, never a
  // navigation.
  const handlePreview = async () => {
    const result = previewCampaignFromForm(form);
    if (!result.ok) {
      toastError(`Preview payload failed validation: ${result.reason}`);
      return;
    }
    try {
      const { token } = await api.createPreviewDraft(result.campaign);
      window.open(`${OFFER_PAGE_BASE}/preview?campaign=${token}`, '_blank');
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to open preview');
    }
  };

  // Sticky-row visibility (task 015-05). Declared BEFORE the error early
  // return below (054-01 case h): a mid-life error — e.g. the post-create
  // re-read failing — must land on the error page, not crash the tree by
  // dropping these hooks between renders ("Rendered fewer hooks than
  // expected"). Declaration position does not change effect timing: the
  // observer still mounts with the form's first committed render.
  const [isVisibleHeader, setIsVisibleHeader] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisibleHeader(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">Error: {error}</div>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/campaigns')}>
          Back to campaigns
        </Button>
      </div>
    );
  }

  const currentStatus = form.status as CampaignStatus;
  // 054-05: the list's sub-label semantics (see STATUS_REASON_LABELS above).
  const statusReasonLabel =
    form.statusReason != null ? (STATUS_REASON_LABELS[form.statusReason] ?? null) : null;

  // Lifecycle button state: derived from status + run window vs now
  // (shared rule — see isWithinRunWindow in @leads-platform/shared). HF-152:
  // the end bound is the ET calendar day, inclusive (endBoundMs) — Start
  // stays enabled through the end date in America/New_York, matching the
  // server's isWithinCampaignDays gate.
  const now = new Date();
  const startMs = DATE_TO_MS(form.startDate);
  const primary = primaryButtonState(currentStatus, startMs, endBoundMs(form.endDate, now));
  const completeDisabled = currentStatus === 'completed';
  // HF-094 (QA-17): Save & reopen shows only for a completed campaign in edit
  // mode. FormState carries no archived flag (grepped — the archived marker
  // lives only on CampaignDetailResponse/campaigns.tsx, not in this form's
  // state), so this gates on status alone; an archived-completed campaign's
  // reopen attempt is refused by the server's own 409 ("Unarchive the
  // campaign before reopening it" — campaign.service.ts).
  const canReopenFromForm = isEdit && currentStatus === 'completed';

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-6xl">
      <PageHeader
        kicker={isEdit ? 'Edit campaign' : 'New campaign'}
        title={isEdit ? form.name : 'New campaign'}
        desc="Unified campaign form with dynamic sections based on campaign style."
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-0"
              onClick={() => navigate('/campaigns')}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-0"
              disabled={submitting || imageUploading}
              onClick={() => void submit('close')}
            >
              Save &amp; close
            </Button>
            {canReopenFromForm && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11 md:min-h-0"
                disabled={submitting || imageUploading}
                onClick={() => void submit('reopen')}
              >
                Save &amp; reopen
              </Button>
            )}
            {/* 069-03 (D4): the create-mode second primary — saves the row
                directly as Scheduled. Enabled only with a start date set — the
                same arming rule the edit-mode Schedule button applies; a plain
                Create still defaults to Pending. Edit mode never renders it —
                the lifecycle buttons own the status there. */}
            {!isEdit && (
              <Button
                type="button"
                className="min-h-11 md:min-h-0"
                disabled={submitting || DATE_TO_MS(form.startDate) == null}
                onClick={() => void submit('stay', { scheduleOnCreate: true })}
              >
                Save &amp; schedule
              </Button>
            )}
            <Button
              type="submit"
              className="min-h-11 md:min-h-0"
              disabled={submitting || imageUploading}
            >
              {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </>
        }
      />
      <div ref={headerRef}></div>

      {/* QA 054 run 1 B-02/B-03: the inline action feedback line, near the
          action buttons above and the lifecycle section below — the failed
          lifecycle POST's api error body (destructive, as the form's field
          errors) and the save/lifecycle success line. Cleared by the next
          action attempt or a successful save; display only. */}
      {actionFeedback && (
        <p
          role={actionFeedback.kind === 'error' ? 'alert' : 'status'}
          className={clsx(
            'text-sm',
            actionFeedback.kind === 'error' ? 'text-destructive' : 'text-accent'
          )}
        >
          {actionFeedback.message}
        </p>
      )}

      {/* ----------------------------------------------------------------
          Status & lifecycle (edit mode only)

          Two buttons: start/schedule/pause (toggles) and complete.
          See primaryButtonState / completeDisabled for the rules.
      ----------------------------------------------------------------- */}
      {isEdit && (
        <FormSection
          title="Status"
          desc="Schedule arms the campaign for its start date; Resume makes it live now; Pause stops it; Complete ends it."
        >
          {/* 054-05: the list's pill + statusReason sub-label (see
              STATUS_REASON_LABELS), the same shape as StatusCell in
              campaigns.tsx. */}
          <div className="sm:col-span-2">
            <StatusPill value={currentStatus} />
            {statusReasonLabel != null && (
              <div className="text-xs text-faint">{statusReasonLabel}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-0"
              disabled={primary.disabled}
              onClick={() => {
                if (primary.action === 'schedule') {
                  const iso = DATE_TO_ISO(form.startDate);
                  if (!iso) {
                    toastError('Set a start date first');
                    return;
                  }
                  runLifecycle(() => api.scheduleCampaign(id!, iso), 'scheduled');
                } else if (primary.action === 'pause') {
                  runLifecycle(() => api.pauseCampaign(id!), 'paused');
                } else {
                  // Activate (not resume) — api.resumeCampaign is preserved in
                  // lib/api.ts but no longer called by the form (034-04 Scope 2).
                  runLifecycle(
                    () => api.activateCampaign(id!),
                    'resumed',
                    (result) => {
                      // The activate endpoint returns activationWarnings when the
                      // campaign passed gate checks but had no passed test run.
                      // ADR-010 §12 / 031 decision 10: warn with info tone, not error —
                      // the status transition already happened.
                      if ('activationWarnings' in result) {
                        const { activationWarnings } = result;
                        if (
                          Array.isArray(activationWarnings) &&
                          activationWarnings.includes('no_passed_test_run')
                        ) {
                          info(
                            'Activated without a passed test run — run a test from Advertiser Send'
                          );
                        }
                        // 067-05 (D5): non-blocking — the same info tone as the
                        // no_passed_test_run warning above.
                        if (
                          Array.isArray(activationWarnings) &&
                          activationWarnings.includes('iterable_missing_journey_id')
                        ) {
                          info(
                            'Activated with an Iterable row missing its journey id — leads will not enter that journey'
                          );
                        }
                      }
                    }
                  );
                }
              }}
            >
              {primary.label === 'start'
                ? 'Resume'
                : primary.label === 'schedule'
                  ? 'Schedule'
                  : 'Pause'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 md:min-h-0"
              disabled={completeDisabled}
              onClick={() => runLifecycle(() => api.completeCampaign(id!), 'completed')}
            >
              Complete
            </Button>
          </div>
          <FieldError messages={fieldErrors('_form')} />
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          1. Identity & association
      ----------------------------------------------------------------- */}
      <FormSection
        title="Identity & association"
        desc="Campaign name, style, advertiser and client. Trading Tips is the only business unit; publisher is internal-only and not selected here."
      >
        <Field label="Campaign name" required span={2}>
          <Input
            placeholder="e.g. BSW Daily Briefing — July"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <FieldError messages={fieldErrors('name')} />
        </Field>

        <Field
          label="Campaign style"
          required
          hint="Sets which sections of this form apply. A user-defined label over one model. “External Form”: the form is hosted on the partner site and POSTs to our /ext interstitial — no hosted page here."
        >
          <Select
            value={form.style}
            onChange={(e) => {
              const newStyle = e.target.value as CampaignStyle;
              const newCampaignStyle = STYLE_LABEL_TO_ENUM[newStyle];
              const wasOptin = form.style === 'Custom Opt-in';
              const willBeOptin = newStyle === 'Custom Opt-in';

              // Clear placement fields when switching away from Custom Opt-in
              if (wasOptin && !willBeOptin) {
                setForm((f) => ({
                  ...f,
                  style: newStyle,
                  campaignStyle: newCampaignStyle,
                  placementSequence: '',
                  placementSpot: '',
                  placementIsThankYouAnchor: false,
                }));
              } else {
                setForm((f) => ({
                  ...f,
                  style: newStyle,
                  campaignStyle: newCampaignStyle,
                  limitType:
                    willBeOptin && !wasOptin
                      ? limitTypeAfterSendMode(f.limitType, f.sendMode)
                      : f.limitType,
                }));
              }
              setValidationErrors({});
            }}
          >
            {CAMPAIGN_STYLES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </Field>

        {/* Epic 043 (task 01): a CTO Email campaign names no advertiser — the
            links are ours and the server records the internal TradingTips
            owner. The field (and its required mark) renders for every other
            style, unchanged. */}
        {!flags.isCtoEmail && (
          <Field label="Advertiser" required>
            <Typeahead<AdvertiserResponse>
              options={advertisers}
              value={form.advertiserId || null}
              onChange={(v) => set('advertiserId', v ?? '')}
              getId={(a) => a.id}
              getLabel={(a) => a.name}
              placeholder="Select an advertiser…"
              error={fieldErrors('advertiserId')}
            />
          </Field>
        )}

        <Field label="Client name" span={2}>
          <Input
            placeholder="e.g. Beacon Street"
            value={form.clientName}
            onChange={(e) => set('clientName', e.target.value)}
          />
        </Field>
      </FormSection>

      {/* ----------------------------------------------------------------
          1a. External form integration (External Form only — epic 096 D4)

          Replaces the Offer page section for this style: the partner hosts
          the form; we host the capture. The Integration URL is the absolute
          POST endpoint (read-only, copyable); the cheat sheet names the exact
          hidden-field keys the partner's form posts (task-02's /ext route
          contract). Beside it the per-campaign error ladder + site axis.
      ----------------------------------------------------------------- */}
      {flags.isExternalForm && (
        <FormSection
          title="External form integration"
          desc="The form itself lives on the partner site; this campaign captures through our /ext interstitial. Point the partner's form at the Integration URL below."
        >
          {/* D4: the Integration URL block — read-only, absolute, copyable.
              Create mode has no id yet, so the URL cannot exist: the block
              shows the same "populates on save" note PageLinkField uses. */}
          <FieldBlock
            label="Integration URL"
            span={2}
            hint="The partner's form POSTs to this URL (form-encoded or JSON). Copy it whole — the campaign ref is part of the path."
          >
            <div className="flex items-center gap-2 rounded-lg bg-paper px-3 py-2 hairline">
              <span className="shrink-0 font-mono text-xs font-semibold uppercase tracking-wide text-faint">
                POST
              </span>
              {extFormPostUrl(id) ? (
                <span
                  data-testid="ext-integration-url"
                  className="min-w-0 flex-1 truncate font-mono text-xs text-ink"
                >
                  {extFormPostUrl(id)}
                </span>
              ) : (
                <span className="min-w-0 flex-0 truncate text-xs italic text-faint">
                  Populates on save, once the campaign exists
                </span>
              )}
              <div className="flex-1"></div>
              <CopyButton value={extFormPostUrl(id)} title="Copy the Integration URL" />
            </div>
          </FieldBlock>

          {/* The hidden-field cheat sheet — the exact body key names of the
              task-02 contract, verbatim (integrators copy the names). */}
          <FieldBlock
            label="Hidden fields"
            span={2}
            hint="The body keys the partner's form sends with every POST."
          >
            <div className="rounded-lg bg-paper p-3 hairline">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">
                Form body keys
              </div>
              <dl className="space-y-1.5">
                {EXT_FORM_HIDDEN_FIELDS.map((field) => (
                  <div key={field.name} className="flex items-baseline gap-2 text-xs">
                    <dt className="font-mono font-semibold text-ink">{field.name}</dt>
                    {field.required && <span className="text-rose">*</span>}
                    <dd className="text-faint">{field.hint}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </FieldBlock>

          {/* D4/GATE-3: the error-ladder target. Empty = the global default
              (tradingtips.com) — the resolve endpoint applies it, the column
              just stays NULL. */}
          <Field
            label="Error/fallback URL"
            span={2}
            hint="Where the visitor goes when the capture is rejected; empty = tradingtips.com."
          >
            {/* type="text", not "url": the native url-input validation would
                block the submit event before our check runs (jsdom and the
                browser alike) — this field's error must be OUR message, at
                this field (AC 4). */}
            <Input
              placeholder="https://…"
              value={form.errorUrl ?? ''}
              onChange={(e) => set('errorUrl', e.target.value)}
            />
            <FieldError messages={fieldErrors('errorUrl')} />
          </Field>

          {/* D5a: the site axis — whose traffic/budget this campaign's leads
              belong to. A select of the site catalog (business keys), '' =
              none; the api rejects a key not in the catalog (400). */}
          <Field
            label="Site"
            hint="Whose traffic/budget this campaign's leads belong to. The FSA campaign gets “fsa”."
          >
            <Select value={form.siteId ?? ''} onChange={(e) => set('siteId', e.target.value)}>
              <option value="">— none —</option>
              {sites.map((s) => (
                <option key={s.id} value={s.siteId}>
                  {s.siteId} · {s.label}
                </option>
              ))}
            </Select>
            <FieldError messages={fieldErrors('siteId')} />
          </Field>

          {/* Epic 109 task 01 (D1/ruling 1): the FB campaign whose spend maps
              to this campaign's site — matched by Meta's record ID, so it is
              NOT gated on this style (other styles may carry it through the
              API); empty = no mapping. Digits only (the wire's regex and the
              client check agree); the api rejects anything else. */}
          <Field
            label="Facebook campaign ID"
            hint="The FB campaign whose spend maps to this campaign's site. Empty = no mapping."
          >
            <Input
              mono
              placeholder="7000512942804"
              inputMode="numeric"
              value={form.fbCampaignId ?? ''}
              onChange={(e) => set('fbCampaignId', e.target.value)}
            />
            <FieldError messages={fieldErrors('fbCampaignId')} />
          </Field>

          {/* D7a (task 07): the header message the FSA visitor sees as the
              confirmation bar after an accepted capture — plain text or HTML,
              rendered on every offer step of the walk. Empty = no bar: the
              payload block below always sends the key for this style and the
              api's clear-to-NULL normalizer (D7b) persists '' as NULL, which
              the /ext interstitial reads as "skip the sessionStorage write".
              A plain textarea, not BlockNote (operator 2026-10-12). */}
          <Field
            label="Thank you message"
            span={2}
            hint="The confirmation bar on the visitor's first and every following offer page. Plain text or HTML; empty = no bar."
          >
            <Textarea
              mono
              rows={3}
              value={form.thankYouMessage}
              onChange={(e) => set('thankYouMessage', e.target.value)}
            />
          </Field>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          2a. Offer page (Custom Opt-in, Paid Social — has offer page)
          CTO Email hides this whole section (045-03); its stored values still
          round-trip because hasHostedPage keeps gating the payload block.
      ----------------------------------------------------------------- */}
      {flags.hasOfferPage && (
        <FormSection
          title="Offer page"
          desc="Page identity, link and copy for the hosted opt-in page. The generated page URL appears after save."
        >
          <Field
            label="Page title"
            span={2}
            hint="Browser/SEO <title> text shown on the hosted opt-in page."
          >
            <Input
              placeholder="e.g. Free Market Briefing — Today's Trade Ideas"
              value={form.pageTitle}
              onChange={(e) => {
                set('pageTitle', e.target.value);
                // Auto-generate pageSlug when slug is empty or not hand-edited (task 015-09)
                if (!slugHandEdited.current || !form.pageSlug.trim()) {
                  set('pageSlug', slugify(e.target.value));
                }
              }}
            />
            <FieldError messages={fieldErrors('pageTitle')} />
          </Field>

          <PageLinkField
            label="Page slug"
            value={form.pageSlug}
            onChange={(v) => {
              set('pageSlug', v);
              slugHandEdited.current = true; // Mark as hand-edited (task 015-09)
            }}
            required
            error={fieldErrors('pageSlug')}
            hint="The slug is the only varying part of the page URL; lists show it alone with a Copy action for the full link."
            inertLink={flags.isOptin}
          />

          {/* D5 (operator decision, 2026-08-26): thankYouMessage is hidden for Custom Opt-in
              — no runtime consumer. Paid Social keeps it as the post-signup INTRO screen text.
              The form-model field and payload path stay intact for all hosted-page styles, so a
              stored value round-trips on edit without loss. */}
          {!flags.isOptin && (
            <Field
              label="Thank you message"
              span={2}
              hint="Text of the post-signup intro screen — shown after successful capture, before the first offer. A visitor with no surviving offers sees this message as their whole post-signup experience."
            >
              <Textarea
                mono
                rows={3}
                value={form.thankYouMessage}
                onChange={(e) => set('thankYouMessage', e.target.value)}
              />
            </Field>
          )}

          {/* Epic 106 (ruling 2026-10-20): the offer-page template selector —
              Standard (the epic-082 look, stored NULL) or Partner hero
              (SPECIAL PARTNER OFFER, stored 'partner-hero-v1'). A stored
              NULL/'default' hydrates Standard. The key rides the payload only
              while Partner hero is selected (absent = the default look, the
              old payload shape). Custom Opt-in only (D8: no other style
              grows templates); the legacy page-HTML picker is gone (QA
              round 3 B-06) — this one picks the look, not a starting HTML. */}
          {flags.isOptin && (
            <Field
              label="Template"
              span={2}
              hint="Which look the offer page uses. Partner hero adds the SPECIAL PARTNER OFFER hero with its own Headline and Intro below."
            >
              <Select
                value={form.offerTemplate ?? ''}
                onChange={(e) => set('offerTemplate', offerTemplateOptionValue(e.target.value))}
              >
                {OFFER_TEMPLATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/* Epic 106: the Partner hero H1 — rendered only under
              partner-hero-v1, above the hero image. React text (escaped,
              never HTML); empty = the layout's headline fallback (the body's
              own heading when it has one). The payload sends it only while
              the template is selected and non-empty (empty ⇒ key omitted). */}
          {isPartnerHero && (
            <Field
              label="Headline"
              span={2}
              hint="The big headline at the top of the Partner hero page. Leave empty to fall back to the content's own heading."
            >
              <Input value={form.headline} onChange={(e) => set('headline', e.target.value)} />
            </Field>
          )}

          {/* Epic 106 (ruling 2026-10-20, review M-1): the Partner hero Hero
              Image — the same Custom Opt-in composite the default look keeps
              at its epic-082/095 slot below the CTA fields (the upload/URL
              model is documented there). The ruling order puts it beside the
              Headline: Headline → Hero Image → Intro → Main Content →
              CTA Text. Exactly one instance renders — this one under
              partner-hero-v1, the other under flags.isOptin && !isPartnerHero
              — so the composite and its campaign-image id mount once however
              the template toggles. */}
          {isPartnerHero && (
            <FieldBlock
              label="Image"
              span={2}
              hint="Upload an image or paste its web address. Shown beside the offer copy. Leave empty for no image."
            >
              <CampaignImageInput
                id="campaign-image"
                value={form.bannerImageUrl}
                onChange={(url) => set('bannerImageUrl', url)}
                onUploadingChange={setImageUploading}
              />
            </FieldBlock>
          )}

          {/* Epic 106 (ruling 2026-10-20): the Partner hero Intro — a second
              MainContentEditor instance (the same BlockNote editor), rendered
              only under partner-hero-v1. Stored as BlockNote HTML in
              intro_html; empty = no intro block on the hero. The payload
              sends it only while the template is selected and non-empty
              (empty ⇒ key omitted). FieldBlock, not Field (HF-011): same
              non-interactive-label problem as Main Content below; the editor
              names its own accessible surface. */}
          {isPartnerHero && (
            <FieldBlock
              label="Intro"
              span={2}
              hint="The short copy inside the Partner hero, above the first button. Leave empty for no intro block."
            >
              <MainContentEditor
                value={form.introHtml}
                onChange={(v) => set('introHtml', v)}
                ariaLabel="Intro"
              />
            </FieldBlock>
          )}

          {/* Epic 042 task 02: the raw-HTML textarea became the "Main Content"
              WYSIWYG (TipTap). Same form key (htmlContent), same storage — an
              HTML fragment in html_content — and the same section gating; the
              template picker above and the prefill effect write this key.
              FieldBlock, not Field (HF-011): Field's <label> would forward every
              click on the non-interactive contenteditable to the label's control
              — the first toolbar button — stealing the caret on each click. The
              editor carries its own role="textbox" + aria-label, so the
              accessible name does not depend on the wrapper. */}
          <FieldBlock
            label="Main Content"
            span={2}
            hint="Rich text for the hosted page — headings, images, links and lists."
          >
            <MainContentEditor value={form.htmlContent} onChange={(v) => set('htmlContent', v)} />
          </FieldBlock>

          {/* Epic 042 task 02 / tasks 037-10: the CTA label with a per-style consumer —
              Custom Opt-in labels the offer Accept action (shown as typed, uppercased by the
              page; empty = platform default "Get My Free Report"); Paid Social labels the welcome
              submit button on the landing page (empty = flowKind default "Get Your FREE
              Report"). The payload always carries the value (visibility-only semantics, like the
              disclaimer) so a stored label round-trips. QA round-1 (task 04, verdicts B-01/#3):
              the label is ALWAYS «CTA Text» — under both templates; the ONE shared label rides
              every CTA the partner-hero look renders. */}
          {(flags.isOptin || flags.isPaidSocial) && (
            <Field
              label="CTA Text"
              span={2}
              hint={
                isPartnerHero
                  ? 'The button text (every button on the page carries it). Leave blank for the platform default “Get My Free Report”.'
                  : flags.isOptin
                    ? 'The Accept button text on the offer page. Leave blank for the platform default “Get My Free Report”.'
                    : 'The submit button text on the paid social landing page. Leave blank for the default “Get Your FREE Report”.'
              }
            >
              <Input value={form.ctaText} onChange={(e) => set('ctaText', e.target.value)} />
            </Field>
          )}

          {/* Epic 082 (D4): the Custom Opt-in page's image, shown beside the offer copy.
              A URL on the campaign's banner_image_url column; a trimmed-empty
              value clears it — the payload always carries it for this style.
              Epic 095 (D1/D8): the field became one composite — a drop zone with
              the URL input beneath it, no tabs, both paths always visible.
              Upload is just a way to produce a URL: the file POSTs on choice
              (D6) and collapses into the minted URL; the model keeps the single
              bannerImageUrl string, and Save is blocked while an upload is in
              in flight. FieldBlock, not Field (HF-011): the composite carries
              several controls with labels of their own, and Field's wrapping
              <label> would forward hint/preview clicks to the file input.
              Epic 106 (review M-1): under partner-hero-v1 the same composite
              renders beside the Headline instead (the ruling order), so this
              slot is the Standard/default look only. */}
          {flags.isOptin && !isPartnerHero && (
            <FieldBlock
              label="Image"
              span={2}
              hint="Upload an image or paste its web address. Shown beside the offer copy. Leave empty for no image."
            >
              <CampaignImageInput
                id="campaign-image"
                value={form.bannerImageUrl}
                onChange={(url) => set('bannerImageUrl', url)}
                onUploadingChange={setImageUploading}
              />
            </FieldBlock>
          )}

          {/* FR-A37 (026-02) — placement fixed by #51 (027-01); scope widened to
              BOTH capture styles by 054-03 (qa #54 / FR-A41): the disclaimer is
              the LAST field of the Offer page section, below the Page HTML
              textarea and its picker/toggle controls, and renders for Paid
              Social (whose welcome page mounts an opt-in form — OptInForm
              gates on captureStrategy === 'email_form') and for Custom Opt-in
              (the offer step renders it under the Accept button —
              OfferActions.resolveOfferDisclaimer; her custom page used to show
              the Paid Social text with no way to change it). Hidden for Co-op
              (no offer step — the payload writes optInDisclaimer only under
              hasHostedPage, which Co-op never has) and for CTO Email (no page,
              045-03) — visibility-only for those two: the form state and the
              always-include payload write are untouched, so a stored value on
              a hidden style keeps round-tripping on save. The per-style hint:
              Custom Opt-in shows the resolved fallback line with the selected
              advertiser's name on lead_post (the send mode that shares the
              lead), and the no-disclaimer CPC line on click_out — the
              fallback line only ever fires on lead_post, HF-082, amends epic
              042 decision 5 (Preview cannot show either — its stub step has
              no advertiserName). Create-mode prefill is style-aware (the effect
              beside the htmlContent prefill): empty for Custom Opt-in, the
              built-in default for Paid Social. Plain HTML textarea — links
              (<a>) must survive editing (admin-requirements.md §3.8); no
              rich-text editor. */}
          {(flags.isPaidSocial || flags.isOptin) && (
            <Field
              label="Disclaimer"
              span={2}
              hint={
                flags.isOptin
                  ? form.sendMode === 'lead_post'
                    ? `Shown under the Accept button on this advertiser's page. Leave empty for the standard line: "By clicking above you agree to share your email with ${selectedAdvertiserName ?? 'the advertiser'} and receive their emails. Unsubscribe anytime."`
                    : "Shown under the Accept button on this advertiser's page. Leave empty for no disclaimer: CPC pages don't share the lead."
                  : 'HTML rendered under the email field on the opt-in form. Links (<a> tags) are preserved. Empty clears it and restores the standard TradingTips disclaimer.'
              }
            >
              <Textarea
                rows={4}
                value={form.optInDisclaimer}
                onChange={(e) => {
                  set('optInDisclaimer', e.target.value);
                  disclaimerHandEdited.current = true;
                }}
              />
              <FieldError messages={fieldErrors('optInDisclaimer')} />
            </Field>
          )}
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          2b. Tracking (all hosted-page styles — hasHostedPage)
          GA click label for non-Co-op campaigns (FR-A32 / 019-04). Placed
          here so CTO Email gets it directly above the CTO links section
          (045-03). Epic 034 task 05 will join this section if it already
          exists.
      ----------------------------------------------------------------- */}
      {flags.hasHostedPage && (
        <FormSection
          title="Tracking"
          desc="Analytics configuration for this campaign's hosted page or links."
        >
          {/* FR-A32 / 019-04: per-campaign GA click label. Stored verbatim; fires as
              the GA event CATEGORY on CTA clicks (legacy 'GA Click Event'). Rendered
              only where a hosted page exists — Co-op has no hosted page and no label
              (legacy Co-op tables carry no ga column). For CTO Email the label fires
              on the link click itself (task 02 interstitial). */}
          <Field
            label="GA click event label"
            span={2}
            hint="Pre-filled from the page slug on new campaigns — edit freely; clearing it disables GA click tracking for this campaign. On clicks of this campaign's page content and submit button — or on CTO link clicks — it becomes the Google Analytics event category of the 'Clicks' event your reports filter on."
          >
            <Input
              maxLength={255}
              value={form.gaEventLabel}
              onChange={(e) => {
                gaLabelHandEdited.current = true; // Any keystroke latches the label (054-04 / Q119)
                set('gaEventLabel', e.target.value);
              }}
            />
          </Field>

          {/* A14 (034-05): Meta CAPI three-state control. "Style default" shows
              which direction the style resolves so the operator understands what
              null means before overriding it. */}
          <Field
            label="Meta CAPI"
            span={2}
            hint="Whether to fire the Meta Conversions API event on lead capture. Style default is on for Paid Social and CTO Email, off for all other styles."
          >
            <Select
              value={form.capiEnabled ?? 'default'}
              onChange={(e) => set('capiEnabled', e.target.value as 'default' | 'on' | 'off')}
            >
              <option value="default">
                {`Style default (${resolveCapiEnabled({ campaignStyle: form.campaignStyle, capiEnabled: null }) ? 'on' : 'off'})`}
              </option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </Select>
          </Field>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          2c. CTO links (CTO Email only — the link generator, §2.9 / FR-A34)
          Placed below Tracking so the Tracking section renders directly above
          it for CTO Email (045-03 placement requirement).

          Create mode (037-06, operator ruling 2026-09-09 — reverses 024
          README decision #5): the live section renders right away with
          client-local draft rows that ride the campaign Save payload. Edit
          mode: rows persist through their own endpoints and never ride the
          Save payload. The saved-style-mismatch arm keeps the note (HF-001 /
          QA #57: mounting against a parent that is not cto_email on the
          server 400s).
      ----------------------------------------------------------------- */}
      {savedStyle === 'CTO Email' && isEdit && id && (
        <CtoLinksSection campaignId={id} sites={sites} />
      )}
      {isEdit && flags.isCtoEmail && savedStyle !== 'CTO Email' && (
        <FormSection
          title="CTO links"
          desc="A CTO Email campaign holds link rows — traffic source × promo type × rate × validations, each with a generated URL."
        >
          <div className="rounded-lg bg-paper p-3 text-xs text-muted hairline sm:col-span-2">
            Save the campaign to add links.
          </div>
        </FormSection>
      )}
      {!isEdit && flags.isCtoEmail && (
        <CtoLinksSection
          mode="create"
          sites={sites}
          onDraftsChange={setCtoLinkDrafts}
          draftErrors={ctoLinkRowErrors}
        />
      )}

      {/* ----------------------------------------------------------------
          4. Consent configuration (journey styles only, per design note)
      ----------------------------------------------------------------- */}
      {flags.isJourney && (
        <FormSection
          title="Consent configuration"
          desc="Each campaign declares what consent was obtained and where the evidence resides. Every lead captured on this campaign records a consent grant with this basis. The basis and evidence are kept for the record; routing and ESP sends don't read them."
        >
          <Field label="Consent basis" required>
            <RadioGroup
              options={['On-Page', 'Upstream'] as const}
              value={form.consentBasis === 'on_page' ? 'On-Page' : 'Upstream'}
              onChange={(v) => set('consentBasis', v === 'On-Page' ? 'on_page' : 'upstream')}
            />
          </Field>
          <div className="rounded-lg bg-paper p-3 text-xs text-muted hairline">
            {form.consentBasis === 'on_page' ? (
              <>
                <span className="font-semibold text-ink">On-Page Consent</span> — disclosure text
                shown on the form. Evidence: form version, submission timestamp, disclosure
                text/version.
              </>
            ) : (
              <>
                <span className="font-semibold text-ink">Upstream Consent</span> — consent
                established in the source newsletter creative or advertiser agreement before the
                request reaches TT. Evidence: source creative ref, disclosure version, advertiser.
              </>
            )}
          </div>
          <Field
            label="Disclosure text / version evidence"
            span={2}
            hint="Reference to the disclosure version, form version, or source creative that establishes consent."
          >
            <Input
              placeholder="e.g. Consent disclosure v2.3 — on-page form"
              value={form.consentEvidence}
              onChange={(e) => set('consentEvidence', e.target.value)}
            />
          </Field>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          5. Eligibility sequence (Paid Social + External Form)

          CTO Email is a journey style but has no per-campaign eligibility
          section: its validations live on each link row (ADR-007 #3, epic 024
          README #4). Epic 096 task 03: External Form joins Paid Social — the
          /ext interstitial's Turnstile wait (requiresTurnstile) reads this
          campaign's cloudflare step, so the operator must be able to add it.
      ----------------------------------------------------------------- */}
      {(flags.isPaidSocial || flags.isExternalForm) && (
        <FormSection
          title="Eligibility sequence"
          desc="Ordered per-campaign plug-in list. Each check returns pass/fail/indeterminate/error with configured behavior. The ordered checks run live at capture already; Turnstile and EmailOversight verify against the real services once production keys are flipped on — dev runs test doubles today."
        >
          <div className="sm:col-span-2 space-y-3">
            {form.eligibilitySteps.map((row, index) => {
              // Kind label/description looked up from the options list; a kind
              // outside the list (defensive — server-validated) renders raw.
              const stepDef = ELIGIBILITY_STEP_KIND_OPTIONS.find((o) => o.value === row.stepKind);
              const kindLabel = stepDef?.label ?? row.stepKind;
              return (
                <div key={row.id} className="rounded-lg border p-4 transition">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <div className="flex items-center gap-3">
                      <span
                        aria-label={`Position ${index + 1}`}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-line bg-paper text-xs font-semibold text-ink"
                      >
                        {index + 1}
                      </span>
                      <div>
                        <div className="text-[13px] font-semibold text-ink">{kindLabel}</div>
                        <div className="mt-0.5 text-xs text-muted">{stepDef?.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-11 md:min-h-0"
                          aria-label={`Move ${kindLabel} up`}
                          onClick={() => moveEligibilityStep(row.id, 'up')}
                        >
                          Up
                        </Button>
                      )}
                      {index < form.eligibilitySteps.length - 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-11 md:min-h-0"
                          aria-label={`Move ${kindLabel} down`}
                          onClick={() => moveEligibilityStep(row.id, 'down')}
                        >
                          Down
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-11 md:min-h-0"
                        onClick={() => removeEligibilityStep(row.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  {stepDef &&
                    (() => {
                      const config = parseEligibilityConfig(row.configJson);
                      const updateConfig = (
                        changes: Parameters<typeof updateEligibilityConfig>[1]
                      ) => {
                        updateEligibilityStep(row.id, {
                          configJson: updateEligibilityConfig(
                            row.configJson,
                            changes,
                            stepDef.value
                          ),
                        });
                      };

                      return (
                        <div className="mt-4 space-y-4">
                          {/* B3: Per-kind honest control surface with descriptions */}
                          {stepDef.value === 'cloudflare' && (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-4">
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When check fails
                                    </span>
                                    <Select
                                      value={config.onFail}
                                      onChange={(e) =>
                                        updateConfig({
                                          onFail: e.target.value as typeof config.onFail,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {BEHAVIOR_ON_FAIL_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Check failed (invalid or missing token). Halt rejects the
                                      lead; Continue runs remaining steps but lead is still
                                      rejected.
                                    </span>
                                  </label>

                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When check errors
                                    </span>
                                    <Select
                                      value={config.onError}
                                      onChange={(e) =>
                                        updateConfig({
                                          onError: e.target.value as typeof config.onError,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {BEHAVIOR_ON_ERROR_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Check malfunctioned (e.g., verifier unreachable). Halt
                                      rejects; Continue tries to complete the record.
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </>
                          )}

                          {stepDef.value === 'email_oversight' && (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-4">
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When check fails
                                    </span>
                                    <Select
                                      value={config.onFail}
                                      onChange={(e) =>
                                        updateConfig({
                                          onFail: e.target.value as typeof config.onFail,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {BEHAVIOR_ON_FAIL_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Check failed (invalid email). Halt rejects the lead; Continue
                                      runs remaining steps but lead is still rejected.
                                    </span>
                                  </label>

                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When check cannot decide
                                    </span>
                                    <Select
                                      value={config.onIndeterminate}
                                      onChange={(e) =>
                                        updateConfig({
                                          onIndeterminate: e.target
                                            .value as typeof config.onIndeterminate,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {BEHAVIOR_ON_INDETERMINATE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Check could not decide (API unavailable). A soft signal never
                                      rejects the lead on its own.
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </>
                          )}

                          {stepDef.value === 'internal_duplicate' && (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-4">
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When duplicate found
                                    </span>
                                    <Select
                                      value={config.onDuplicate ?? 'continue'}
                                      onChange={(e) =>
                                        updateConfig({
                                          onDuplicate: e.target.value as NonNullable<
                                            typeof config.onDuplicate
                                          >,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {ON_DUPLICATE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Allow creates a soft signal (lead may be captured); Block
                                      hard-rejects. A duplicate is not automatically ineligible.
                                    </span>
                                  </label>

                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-inc">
                                      When check errors
                                    </span>
                                    <Select
                                      value={config.onError}
                                      onChange={(e) =>
                                        updateConfig({
                                          onError: e.target.value as typeof config.onError,
                                        })
                                      }
                                      className="w-36 h-8 text-xs"
                                    >
                                      {BEHAVIOR_ON_ERROR_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <span className="text-xs text-muted max-w-36">
                                      Check malfunctioned (database failure). Halt rejects; Continue
                                      tries to complete the record.
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                </div>
              );
            })}
            {ELIGIBILITY_STEP_KIND_OPTIONS.filter(
              (stepDef) => !form.eligibilitySteps.some((row) => row.stepKind === stepDef.value)
            ).map((stepDef) => (
              <div key={stepDef.value} className="rounded-lg border p-4 transition">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <div>
                    <div className="text-[13px] font-semibold text-ink">{stepDef.label}</div>
                    <div className="mt-0.5 text-xs text-muted">{stepDef.description}</div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 md:min-h-0"
                    onClick={() => addEligibilityStep(stepDef.value)}
                  >
                    Add
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="sm:col-span-2">
            <Banner tone="note" title="No do-not-mail check here.">
              Suppression applies to email sending only (Q58). It is not modeled as an eligibility
              step. A suppressed address may still be stored internally and continue to offers.
            </Banner>
          </div>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          6. ESP destinations (journey styles only, per design note) —
          ONE shared component (components/esp-destinations-section.tsx,
          epic 101 fix 06 D7): the catalog, the offeredFor filter, the
          Iterable warn banner and the table/controls all live there, so a
          connector-list change lands once and applies to every style that
          renders the section. The rows themselves stay this form's state.
      ----------------------------------------------------------------- */}
      {flags.hasEspDestinations && (
        <EspDestinationsSection
          campaignStyle={form.campaignStyle}
          espDestinations={form.espDestinations}
          onRowsChange={(rows) => setForm((f) => ({ ...f, espDestinations: rows }))}
          onTouched={() => {
            // 054-08: any manual edit latches the seed off — a removed
            // default never comes back.
            espTouched.current = true;
          }}
        />
      )}

      {/* ----------------------------------------------------------------
          8. Partner Send (Co-op + Custom Opt-in only, per design note)
      ----------------------------------------------------------------- */}
      {flags.hasPartnerSend && (
        <FormSection title="Advertiser Send (CRM URLs)" desc={PARTNER_SEND_DESC}>
          {/* 085 D9: every partner-config issue; the cell collapses when there is none. */}
          <div className="sm:col-span-2 empty:hidden">
            <PartnerConfigIssues issues={partnerConfigIssues} />
          </div>
          {/* Switch warnings (scope 3) — inline banners, never a dialog (006
              decision 12). Edit mode only: a new campaign has no loaded mode to
              differ from, so loadedSendVia is null and neither renders. */}
          {loadedSendVia === 'simple' && sendVia === 'endpoint' && (
            <div className="sm:col-span-2">
              <Banner tone="warn" title="Switching to an advertiser endpoint:">
                {SWITCH_ONTO_ENDPOINT_WARNING}
              </Banner>
            </div>
          )}
          {loadedSendVia === 'endpoint' && sendVia === 'simple' && (
            <div className="sm:col-span-2">
              <Banner tone="warn" title="Leaving the advertiser endpoint:">
                {SWITCH_OFF_ENDPOINT_WARNING}
              </Banner>
            </div>
          )}

          {/* Send-mode toggle is Custom Opt-in only (Co-op is lead_post only) */}
          {flags.isOptin && (
            <Field
              label="Send mode"
              hint="Pick the method your advertiser's documentation specifies. If they support both GET and POST, choose GET — it's the simpler option."
            >
              <RadioGroup
                options={['Lead post to advertiser CRM', 'Direct CPC (click-out)'] as const}
                value={
                  form.sendMode === 'lead_post'
                    ? 'Lead post to advertiser CRM'
                    : 'Direct CPC (click-out)'
                }
                onChange={(v) => {
                  const nextMode = v === 'Lead post to advertiser CRM' ? 'lead_post' : 'click_out';
                  // Endpoint mode is lead-post only (ADR-016 §2) — an endpoint
                  // carries no direct_url. Going click-out drops back to Simple
                  // URL and the off-endpoint banner explains what the save does;
                  // the endpoint and its values stay in state, so switching back
                  // to lead post restores the picked endpoint unsaved.
                  setForm((f) => ({
                    ...f,
                    sendMode: nextMode,
                    sendVia: nextMode === 'click_out' ? 'simple' : f.sendVia,
                    limitType: limitTypeAfterSendMode(f.limitType, nextMode),
                  }));
                }}
              />
            </Field>
          )}

          {/* Send via (scope 2): lead-post only. Switching it never clears the
              other mode's fields — only the save applies the payload rule. */}
          {(flags.isCoop || form.sendMode === 'lead_post') && (
            <Field
              label="Send via"
              hint="Simple URL for an advertiser whose every campaign is its own signup URL; Advertiser endpoint for one whose call is defined once and reused per flight."
            >
              <RadioGroup
                options={['Simple URL', 'Advertiser endpoint']}
                value={form.sendVia === 'endpoint' ? 'Advertiser endpoint' : 'Simple URL'}
                onChange={(v) =>
                  set('sendVia', v === 'Advertiser endpoint' ? 'endpoint' : 'simple')
                }
              />
            </Field>
          )}

          {/* Partner endpoint mode (scope 4 + 5): the picker, then the endpoint's
              declared variables. Headers and bodies live only on the endpoint
              (ADR-016 §7) and the inline transport fields below are replaced —
              rendering them here would show values the save strips. */}
          {sendVia === 'endpoint' &&
            (endpointPickerEmpty ? (
              <FieldBlock label="Advertiser endpoint" span={2}>
                <p className="text-xs leading-relaxed text-muted">
                  No advertiser endpoints for this advertiser yet — create one on the{' '}
                  <Link
                    to="/advertiser-endpoints"
                    className="text-accent underline-offset-2 hover:underline"
                  >
                    Advertiser endpoints screen
                  </Link>
                </p>
              </FieldBlock>
            ) : (
              <Field
                label="Advertiser endpoint"
                required
                span={2}
                hint={
                  form.advertiserId
                    ? 'Method, URL, headers, body and credential come from the endpoint. Retired endpoints are listed only when this campaign already points at one.'
                    : 'Pick an advertiser first — endpoints are defined per advertiser.'
                }
              >
                <Select
                  value={endpointId}
                  disabled={!form.advertiserId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    const next = endpoints.find((row) => row.id === nextId);
                    if (next) seenEndpoints.current.set(next.id, next);
                    setForm((f) => {
                      // Changing the endpoint keeps the values whose keys the new
                      // endpoint also declares and drops the others — the same
                      // literal under the same name is nearly always the same
                      // value, and a surplus key is dead data the engine ignores.
                      // Clearing the picker keeps them all: re-picking the row's
                      // endpoint must not cost the operator their typing.
                      if (!next) return { ...f, endpointId: nextId };
                      const declared = new Set(next.variables.map((variable) => variable.key));
                      const kept: Record<string, string> = {};
                      for (const [key, value] of Object.entries(f.endpointValues ?? {})) {
                        if (declared.has(key)) kept[key] = value;
                      }
                      return { ...f, endpointId: nextId, endpointValues: kept };
                    });
                  }}
                >
                  <option value="">— select an endpoint —</option>
                  {activeEndpoints.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                  {endpointId !== '' && !endpointInActiveList && (
                    <option value={endpointId}>
                      {endpointOptionLabel(endpointId, pickedEndpoint)}
                    </option>
                  )}
                </Select>
                <FieldError
                  messages={[
                    ...(endpointsError ? [endpointsError] : []),
                    ...(fieldErrors('crmConfig.endpoint_id') ?? []),
                  ]}
                />
              </Field>
            ))}

          {sendVia === 'endpoint' && endpointVariables.length > 0 && (
            <>
              <div className="sm:col-span-2">
                <span className="text-xs font-semibold text-ink">Campaign values</span>
                <p className="mt-1 text-xs leading-snug text-faint">
                  {ENDPOINT_VALUES_NO_SECRETS_HINT}
                </p>
              </div>
              {endpointVariables.map((variable) => (
                <Field
                  key={variable.key}
                  label={variable.label}
                  required={variable.required}
                  hint={variable.hint}
                >
                  <Input
                    value={endpointValues[variable.key] ?? ''}
                    placeholder={variable.default}
                    onChange={(e) => setEndpointValue(variable.key, e.target.value)}
                  />
                  <FieldError messages={fieldErrors(`crmConfig.values.${variable.key}`)} />
                </Field>
              ))}
            </>
          )}

          {/* Endpoint mode credential + response phrases (034-03, Scope 4):
              read-only summary under the variables. The endpoint owns these
              — the save carries neither auth nor response_rules (ADR-016 §2;
              INLINE_TRANSPORT_KEYS strips both; CRM_RENDERED_KEYS prevents
              them reaching crmConfigRest). */}
          {sendVia === 'endpoint' && pickedEndpoint && (
            <FieldBlock label="Credential & response phrases" span={2}>
              <p className="text-xs leading-relaxed text-ink">
                <span className="font-medium">Credential:</span>{' '}
                {pickedEndpoint.credentialLabel
                  ? `${pickedEndpoint.credentialLabel}${pickedEndpoint.credentialHint ? ` — ${pickedEndpoint.credentialHint}` : ''}`
                  : 'none'}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-ink">
                <span className="font-medium">Response phrases:</span>{' '}
                {formatEndpointPhrases(pickedEndpoint.transport.response_rules)}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-faint">
                Set on the advertiser endpoint.
              </p>
            </FieldBlock>
          )}

          {/* Lead-post inline fields: Simple URL mode only, and only when
              sendMode='lead_post' (or Co-op, which has no toggle) */}
          {sendVia === 'simple' && (form.sendMode === 'lead_post' || flags.isCoop) && (
            <>
              <Field label="Send method" hint="One method per campaign.">
                <Select value={form.sendMethod} onChange={(e) => set('sendMethod', e.target.value)}>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </Select>
              </Field>

              <Field
                label="Placeholder syntax"
                hint="Per-advertiser, not global — the largest Co-op advertiser uses bracket placeholders."
              >
                <Select value={form.sendSyntax} onChange={(e) => set('sendSyntax', e.target.value)}>
                  {PLACEHOLDER_SYNTAXES.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* D2 (item 13): the delivery-target link is required — the server
                  gate on active|scheduled enforces it (see assertDeliveryTargetReady). */}
              <Field
                label={`Advertiser CRM URL (${form.sendMethod})`}
                required
                span={2}
                hint="Placeholder substitution: email is mandatory; name and site_id optional."
              >
                <Input
                  mono
                  ref={sendUrlRef}
                  value={form.sendUrl}
                  onChange={(e) => set('sendUrl', e.target.value)}
                  placeholder="https://advertiser.example.com/in?email=%email%&sid=%site_id%"
                />
              </Field>

              <div className="sm:col-span-2">
                <Shortcodes
                  codes={CRM_PLACEHOLDERS[form.sendSyntax] || CRM_PLACEHOLDERS.percent}
                  onInsert={(code) => {
                    setForm((f) => ({ ...f, sendUrl: f.sendUrl + code }));
                    // Focus input and move cursor to end so insertion is visible
                    setTimeout(() => {
                      const el = sendUrlRef.current;
                      if (el) {
                        el.focus();
                        el.setSelectionRange(el.value.length, el.value.length);
                      }
                    }, 0);
                  }}
                  label="CRM URL placeholders — click to insert"
                />
              </div>

              {/* Advanced disclosure (034-03, Scope 3): credential picker +
                  response phrase overrides. Collapsed by default; expanded on
                  load when the row already has a credential or any phrase. */}
              <div className="sm:col-span-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-fit min-h-11 md:min-h-0"
                  aria-expanded={advancedOpen}
                  onClick={() => setAdvancedOpen((open) => !open)}
                >
                  Advanced
                </Button>
                {advancedOpen && (
                  <div className="mt-4 flex flex-col gap-4">
                    {/* Credential picker: advertiser-scoped; reload on advertiser
                        change; the stored revoked credential stays listed, marked.
                        The label shows label — hint only — no secret (ADR-011). */}
                    <Field
                      label="Credential"
                      hint={
                        form.advertiserId
                          ? 'Advertiser-scoped. Leave blank for unauthenticated.'
                          : 'Pick an advertiser first — credentials are defined per advertiser.'
                      }
                    >
                      <Select
                        value={storedCredentialId}
                        disabled={!form.advertiserId}
                        onChange={(e) => set('credentialId', e.target.value)}
                      >
                        <option value="">— none —</option>
                        {activeCredentials.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                            {c.hint ? ` — ${c.hint}` : ''}
                          </option>
                        ))}
                        {storedCredential && isStoredCredentialRevoked && (
                          <option value={storedCredential.id}>
                            {storedCredential.label}
                            {storedCredential.hint ? ` — ${storedCredential.hint}` : ''} (revoked)
                          </option>
                        )}
                      </Select>
                      {form.advertiserId && activeCredentials.length === 0 && !storedCredential && (
                        <p className="text-xs text-faint">
                          No credentials yet —{' '}
                          <Link
                            to="/advertiser-credentials"
                            className="text-accent underline-offset-2 hover:underline"
                          >
                            add one on the Advertiser credentials screen
                          </Link>
                          .
                        </p>
                      )}
                    </Field>

                    {/* Response phrase overrides (034-03, Scope 3). Placeholders
                        show the ADR-010 §6 defaults; an empty field omits the key
                        so the classifier falls back to its defaults. */}
                    <Field label="Accepted phrases">
                      <Input
                        value={form.acceptedContains ?? ''}
                        placeholder="ok, success"
                        onChange={(e) => set('acceptedContains', e.target.value)}
                      />
                    </Field>
                    <Field label="Duplicate phrases">
                      <Input
                        value={form.duplicateContains ?? ''}
                        placeholder="duplicate"
                        onChange={(e) => set('duplicateContains', e.target.value)}
                      />
                    </Field>
                    <Field label="Rejected phrases">
                      <Input
                        value={form.rejectedContains ?? ''}
                        onChange={(e) => set('rejectedContains', e.target.value)}
                      />
                    </Field>
                    <p className="text-xs leading-snug text-faint">
                      Comma-separated. Leave blank for the defaults: a 2xx reply is accepted unless
                      it contains a duplicate phrase (default &#xAB;duplicate&#xBB;) or a rejected
                      phrase.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Direct CPC URL: Custom Opt-in, click-out mode. D2 (item 13): the
              delivery-target link is required — enforced by the same server
              gate as the Partner CRM URL field above. */}
          {flags.isOptin && form.sendMode === 'click_out' && (
            <Field
              label="Direct CPC URL"
              required
              span={2}
              hint="Where the offer click is sent. Offer pages only — Co-op campaigns redirect nowhere."
            >
              <Input
                mono
                value={form.directCpcUrl}
                onChange={(e) => set('directCpcUrl', e.target.value)}
                placeholder="https://zenectwealth.com/offer/swing-alert"
              />
            </Field>
          )}

          {/* Click destination URL: Custom Opt-in, lead post mode (HF-083).
              Optional — the lead is posted to the advertiser regardless; this
              only controls where the visitor's click opens. Bound to the
              same form field/config key as the click-out URL above, so a
              stored value survives switching send mode back and forth. */}
          {flags.isOptin && form.sendMode === 'lead_post' && (
            <Field
              label="Click destination URL"
              span={2}
              hint="Optional. Where the browser goes when the visitor clicks the offer; the lead is still posted to the advertiser. %email% is replaced with the visitor's email."
            >
              <Input
                mono
                value={form.directCpcUrl}
                onChange={(e) => set('directCpcUrl', e.target.value)}
                placeholder="https://kryptonstreet.com/sms-optin-cto-new/?email=%email%"
              />
            </Field>
          )}

          {/* Run a test (034-04): edit mode shows the panel and a link to the
              full Test Runs screen; create mode shows only the save-first hint.
              Click-out Custom Opt-in shows the panel too — AdminRunPanel handles
              test_run_not_applicable itself; we do not pre-empt it. */}
          <div className="sm:col-span-2 space-y-3">
            <h3 className="text-sm font-semibold text-ink">Run a test</h3>
            {/* 054-01: create-only. After a create-and-stay the form IS edit
                mode — the panel must not sit under "Save the campaign first". */}
            {!isEdit && (
              <p className="text-xs text-muted">
                Save the campaign first — a test runs against the saved configuration.
              </p>
            )}
            {isEdit && id && (
              <>
                <AdminRunPanel
                  campaignId={id}
                  campaignName={form.name}
                  embeddedInForm
                  onCreated={(run) => {
                    setFormRunId(run.id);
                    setFormRunDetail(null);
                    // Load the detail immediately with the known run id; polling
                    // will continue from the next refetchFormRun tick.
                    getRun(id, run.id)
                      .then(setFormRunDetail)
                      .catch(() => {
                        // Initial load failure — polling will retry.
                      });
                  }}
                />
                {formRunDetail && (
                  <RunDetail
                    detail={formRunDetail}
                    timedOut={formRunTimedOut}
                    onRefresh={formRunRefresh}
                  />
                )}
                <p className="text-xs">
                  <Link
                    to={`/test-runs/${id}`}
                    className="text-accent underline-offset-2 hover:underline"
                  >
                    All test runs for this campaign →
                  </Link>
                </p>
              </>
            )}
          </div>

          {/* Skip Duplicates (epic 057 FR-B35 / ADR-010 A43, handed to this
              epic — 057 README "The split with 054"): writes the canonical
              crm_config.skip_duplicates key through the rest bag (HF-019).
              Gated exactly like the section — hasPartnerSend, so it renders
              for Co-op and Custom Opt-in alike; 057's engine reads the
              receiving campaign's flag with no style filter. */}
          <div className="sm:col-span-2 space-y-2">
            <div className="inline-flex items-center gap-2 text-sm">
              <input
                id="skip-duplicates"
                type="checkbox"
                checked={form.crmConfigRest?.skip_duplicates === true}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    crmConfigRest: {
                      ...(f.crmConfigRest ?? {}),
                      skip_duplicates: e.target.checked,
                    },
                  }))
                }
              />
              <label htmlFor="skip-duplicates">Skip Duplicates</label>
            </div>
            <p className="text-xs leading-snug text-faint">
              Off: a partner reply of 'duplicate' still counts toward this campaign's caps and
              billing (today's Zacks behaviour). On: a duplicate does not use a slot and is not
              billed (StockEarnings behaviour).
            </p>
          </div>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          9. Placement (Custom Opt-in only, per epic-004 A-03)
      ----------------------------------------------------------------- */}
      {flags.isOptin && (
        <FormSection
          title="Placement"
          desc="Where this page is in the offer walk. Custom pages are placed automatically."
        >
          {/* D4 (operator decision, 2026-08-26): the thank-you-anchor mechanism is dropped —
              walk-terminal is now the static neutral page. No anchor control or copy renders;
              the form-model field and payload passthrough stay intact so stored values
              round-trip untouched. */}
          <p className="text-xs leading-snug text-faint" data-testid="placement-status">
            {form.loadedPlacement?.sequence != null && form.loadedPlacement?.spot != null ? (
              <>
                In the walk: Sequence {form.loadedPlacement.sequence}, spot{' '}
                {form.loadedPlacement.spot}. New pages join at the end and move up as they earn. Pin
                a page or change the order on{' '}
                <Link to="/page-order" className="text-accent underline-offset-2 hover:underline">
                  Page Order
                </Link>
                .
              </>
            ) : (
              <>Not in the walk yet. It joins the end of the walk when you Schedule or Start it.</>
            )}
          </p>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          10. Pricing & limits (renamed from "Money & limits")
      ----------------------------------------------------------------- */}
      {/* HF-076: a CTO lead's distribution job routes to co-op candidates and
          evaluates each candidate's own settings, never the CTO campaign's —
          Pricing & limits is never read for CTO Email. Render-only gate: the
          form-model fields and payload passthrough stay intact, so stored
          values round-trip untouched (same precedent as D4 above). */}
      {!flags.isCtoEmail && (
        <FormSection title="Pricing & limits" desc={HF110_COPY[copyMode].pricing}>
          <Field
            label={flags.isOptin ? 'Offer ads cost (CPC)' : 'Cost per lead'}
            required={flags.hasPartnerSend}
          >
            <Input
              mono
              placeholder={flags.isOptin ? '0.45' : '1.85'}
              value={form.pricePerLead}
              onChange={(e) => set('pricePerLead', e.target.value)}
            />
            <FieldError messages={fieldErrors('pricePerLead')} />
          </Field>

          {/* 066 D4: the manual pin, Co-op only. The revenue order (D1) is the
              default route; a Priority number puts this campaign ahead of it.
              Clearing the field sends null on update (D4a) so a pin can be
              removed. */}
          {flags.isCoop && (
            <Field
              label="Priority"
              hint="Leave empty to route by Cost per lead. A number pins this campaign ahead of the revenue order — 1 is tried first."
            >
              <Input
                mono
                placeholder="1"
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                // The Field hint lives inside the wrapping <label>, so it would
                // fold into this input's accessible name — which contains the
                // words "Cost per lead" and made every /cost per lead/i query in
                // the form tests match two inputs. The aria-label pins the name
                // to the visible label alone.
                aria-label="Priority"
              />
              <FieldError messages={fieldErrors('priority')} />
            </Field>
          )}

          <Field label={flags.isOptin ? 'Daily cap' : 'Daily lead cap'} hint="Empty = unlimited.">
            <Input
              mono
              placeholder="1200"
              value={form.dailyCap}
              onChange={(e) => set('dailyCap', e.target.value)}
            />
          </Field>

          {/* Epic 091 D9: the Co-op Facebook share of the daily cap. The live line
              shows what the number means today (facebookShareLimit, the rule's own
              arithmetic); the note flags a site-101 restriction, which runs first
              and skips every Facebook lead whatever the share (D8). */}
          {flags.isCoop && (
            <Field
              label="Max Facebook share of daily cap"
              hint="Empty = no limit. 0% = no Facebook leads. Facebook = site 101. Once today's Facebook sales reach this share of the daily cap, more Facebook leads go to the next partner; CTO leads keep flowing up to the cap."
            >
              <div className="relative">
                <Input
                  mono
                  className="pr-8"
                  placeholder="40"
                  inputMode="numeric"
                  value={form.maxFacebookSharePct ?? ''}
                  onChange={(e) => set('maxFacebookSharePct', e.target.value)}
                  // The Field hint lives inside the wrapping <label> and would fold
                  // into this input's accessible name (the Priority precedent).
                  aria-label="Max Facebook share of daily cap"
                />
                <span
                  className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-faint"
                  aria-hidden="true"
                >
                  %
                </span>
              </div>
              <FieldError messages={fieldErrors('maxFacebookSharePct')} />
              {(() => {
                const shareText = (form.maxFacebookSharePct ?? '').trim();
                if (!shareText) return null;
                const share = Number(shareText);
                const capNum = Number(form.dailyCap);
                const limit =
                  Number.isInteger(share) && share >= 0 && share <= 100
                    ? facebookShareLimit(
                        share,
                        form.dailyCap.trim() && Number.isFinite(capNum) ? Math.trunc(capNum) : null
                      )
                    : undefined;
                const facebookRestricted = sites.some(
                  (site) =>
                    site.siteId === FACEBOOK_SITE_ID && form.restrictedSiteIds.includes(site.id)
                );
                return (
                  <>
                    {limit !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        {limit === null
                          ? 'needs a Daily lead cap'
                          : limit === 0 && share === 0
                            ? '= no Facebook leads'
                            : `= at most ${limit} Facebook leads a day`}
                      </p>
                    )}
                    {facebookRestricted && (
                      <p className="text-xs text-muted-foreground">
                        Site 101 (Facebook) is also restricted below, which skips every Facebook
                        lead first. Remove it there to let this share work.
                      </p>
                    )}
                  </>
                );
              })()}
            </Field>
          )}

          {flags.isCoop && isEdit && id && savedStyle === 'Co-op' && (
            <div className="sm:col-span-2">
              <SourceMixPanel campaignId={id} />
            </div>
          )}

          <Field label="Limit" hint="Empty = unlimited.">
            <Input
              mono
              placeholder="25000"
              value={form.limitValue}
              onChange={(e) => set('limitValue', e.target.value)}
            />
          </Field>

          <Field
            label="Limit type"
            hint={
              flags.isCoop
                ? 'Locked to Unique leads for Co-op campaigns.'
                : HF110_COPY[copyMode].limitType
            }
          >
            <Select
              value={form.limitType}
              onChange={(e) => set('limitType', e.target.value)}
              disabled={flags.isCoop}
            >
              <option value="">None</option>
              {LIMIT_TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="sm:col-span-2 rounded-lg bg-paper p-3 text-xs text-muted hairline">
            <span className="font-semibold text-ink">When a cap is hit:</span>{' '}
            {HF110_COPY[copyMode].capNote}
          </div>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          11. Restrictions (moved directly under Pricing & limits, per Jul-29 call)
      ----------------------------------------------------------------- */}
      {/* HF-076: never evaluated for a CTO lead (see the Pricing & limits gate
          above) — render-only gate, form-model field and payload stay intact. */}
      {!flags.isCtoEmail && (
        <FormSection title="Restrictions" desc={HF110_COPY[copyMode].restrictions}>
          <Field
            label="Blocked email domains"
            span={2}
            hint="Comma-separated email domains to skip, e.g. yahoo.com, aol.com."
          >
            <Input
              mono
              placeholder="yahoo.com, aol.com"
              value={form.domainRestrictions}
              onChange={(e) => set('domainRestrictions', e.target.value)}
            />
          </Field>

          {/* HF-107: the restricted site IDs picker renders for every style again, Co-op included (reverses 023-01). */}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="flex items-center gap-2 text-xs font-semibold text-ink">
              Restricted site IDs
            </span>
            <SiteIdPicker
              options={sites.map((s) => ({ id: s.id, siteId: s.siteId, label: s.label }))}
              selected={form.restrictedSiteIds}
              onToggle={(id) => toggleRestrictedSite(id, !form.restrictedSiteIds.includes(id))}
              onClear={() => set('restrictedSiteIds', [])}
            />
            <span className="text-xs leading-snug text-faint">
              {HF110_COPY[copyMode].siteIdsHelper} Type to search by site ID or name.
            </span>
          </div>

          <FieldBlock label="Geo" span={2}>
            <Toggle
              checked={form.usOnly}
              onChange={(v) => set('usOnly', v)}
              label="Enable only for US users"
              hint="US-only audience."
            />
          </FieldBlock>
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          12. Suppression (moved after Restrictions, per Jul-29 call)
      ----------------------------------------------------------------- */}
      {/* HF-076: never evaluated for a CTO lead (see the Pricing & limits gate
          above) — render-only gate, form-model field and payload stay intact. */}
      {!flags.isCtoEmail && (
        <FormSection title="Suppression" desc={HF110_COPY[copyMode].suppression}>
          {/* #49: the server derived that the linked file cannot be read, so the walk
              is running suppression fail-open — this campaign is not suppressed at
              all until the file is replaced. Loaded from the campaign read only. */}
          {form.suppressionEnabled && form.suppressionFileUnreadable && (
            <div className="sm:col-span-2">
              <Banner tone="warn">
                Linked suppression file is unreadable on the server — suppression is being skipped
                for this campaign. Re-upload the file.
              </Banner>
            </div>
          )}

          <div className="sm:col-span-2">
            <Banner tone="note" title="Self-serve upload.">
              These fields were marked "Only Developer Use" in the legacy admin. In the rebuild, ops
              staff upload and manage suppression files directly.
            </Banner>
          </div>

          <div className="inline-flex items-center gap-2 text-sm">
            <input
              id="suppression-enabled"
              type="checkbox"
              checked={form.suppressionEnabled}
              onChange={(e) => set('suppressionEnabled', e.target.checked)}
            />
            <label htmlFor="suppression-enabled">Suppression enabled</label>
          </div>

          {form.suppressionEnabled && (
            <>
              <div className="space-y-2 sm:col-span-2">
                <span className="text-xs font-semibold text-ink">Hash algorithm</span>
                <Select value="md5" disabled className="w-full max-w-[200px]">
                  {HASH_ALGORITHM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>

              <Field label="Pre-ping API URL" span={2} hint="Supports %email% placeholder.">
                <Input
                  mono
                  placeholder="https://advertiser.example.com/preping?email=%email%"
                  value={form.suppressionPrepingUrl}
                  onChange={(e) => set('suppressionPrepingUrl', e.target.value)}
                />
              </Field>
              {/* 085 D9: the pre-ping's own issues, repeated under its field. */}
              <div className="sm:col-span-2 empty:hidden">
                <PartnerConfigIssues
                  issues={partnerConfigIssues}
                  field="suppression.prepingApiUrl"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <FileDropZone
                  id="suppression-file-input"
                  label="Upload suppression file (CSV or TXT, MD5-hashed)"
                  accept=".csv,.txt"
                  value={form.suppressionPendingFile}
                  onChange={selectSuppressionFile}
                  hint={
                    <>
                      <p className="text-sm text-muted-foreground">
                        The file is uploaded when you click Create/Update.
                      </p>
                      {/* #45: the linked file renders by name in every pending-file
                          state. While the name resolves (undefined) the line waits;
                          a resolution that fails or finds the id unlisted renders
                          the explicit "(name unavailable)" fallback — a bare
                          numeric id is never shown. */}
                      {isEdit && form.suppressionFileId && suppressionFileName !== undefined && (
                        <p className="text-sm text-muted-foreground">
                          Linked file:{' '}
                          {suppressionFileName ??
                            `file #${form.suppressionFileId} (name unavailable)`}
                        </p>
                      )}
                    </>
                  }
                />
              </div>
            </>
          )}
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          13. Schedule & status
      ----------------------------------------------------------------- */}
      <FormSection
        title="Schedule & status"
        desc={
          flags.isCtoEmail
            ? 'Status only — CTO Email capture is gated by each link, not by a campaign date range. The schedule sweep still marks an active campaign completed after its stored end date; Status reflects that (see the CTO links section above).'
            : 'Pick the run window on one calendar; the range shows as MMM D, YYYY – MMM D, YYYY. Co-op and Custom Opt-in campaigns need both dates; Paid Social may leave an end open (… marks it). The start date is what Schedule arms.'
        }
      >
        {/* HF-076: CTO Email capture is gated by the link's own status
            (cto-resolve.service.ts), not the campaign's dates — the field
            doesn't render, but form.startDate/endDate stay in the form model
            and payload untouched (render-only gate, same precedent as above). */}
        {!flags.isCtoEmail && (
          <Field
            label="Date range"
            span={2}
            required={flags.isCoop || flags.isOptin}
            hint={
              flags.isCoop || flags.isOptin
                ? 'Required for Co-op and Custom Opt-in campaigns.'
                : undefined
            }
          >
            {/* #47: every calendar interaction reports the pair it now holds, so a
                lone pick replaces the range — the opposite bound is written as
                no-date here and never survives as a stale value. */}
            <DateRangeField
              from={form.startDate}
              to={form.endDate}
              onChange={(from, to) => {
                set('startDate', from);
                set('endDate', to);
              }}
            />
            <FieldError messages={fieldErrors('dateRange')} />
          </Field>
        )}
        {/* 054-05: the same status pill as the Status section (edit mode) or
            the create-mode note, plus the legend — what each status means,
            when the sweep moves it, and who changes it (the lifecycle buttons,
            true in create mode too, where the Status section does not render). */}
        <Field label="Status" span={2}>
          {isEdit ? (
            <div>
              <StatusPill value={currentStatus} />
              {statusReasonLabel != null && (
                <div className="text-xs text-faint">{statusReasonLabel}</div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              A new campaign is saved as Pending — or, with a start date set, Save &amp; schedule
              creates it directly as Scheduled. On a saved campaign, Schedule or Resume sit at the
              top of the form.
            </p>
          )}
          <div className="space-y-0.5 text-xs text-muted-foreground">
            <p>Pending — saved, not sending.</p>
            <p>Scheduled — armed; goes live at the start date.</p>
            <p>Active — live.</p>
            <p>Completed — ended at the end date or the lead cap.</p>
            <p>
              Pending / Scheduled / Active is changed with the lifecycle buttons at the top of a
              saved campaign.
            </p>
          </div>
        </Field>
      </FormSection>

      {/* ----------------------------------------------------------------
          14. ClickMagick config (Custom Opt-in only)
      ----------------------------------------------------------------- */}
      {flags.isOptin && (
        <FormSection
          title="ClickMagick config"
          desc="Name up to 32 chars, slug up to 20 chars. Link details are stored today; pulling ClickMagick statistics lands in R2 and creating links automatically is post-R1."
        >
          <div className="inline-flex items-center gap-2 text-sm">
            <input
              id="cm-enabled"
              type="checkbox"
              checked={form.cmEnabled}
              onChange={(e) => set('cmEnabled', e.target.checked)}
            />
            <label htmlFor="cm-enabled">ClickMagick enabled</label>
          </div>

          {form.cmEnabled && (
            <>
              <Field label="Domain">
                <Input value={form.cmDomain} onChange={(e) => set('cmDomain', e.target.value)} />
              </Field>

              <Field label="Name (max 32)">
                <Input
                  maxLength={32}
                  value={form.cmName}
                  onChange={(e) => set('cmName', e.target.value)}
                />
                <FieldError messages={fieldErrors('cmConfig.name')} />
              </Field>

              <Field label="Slug (max 20)">
                <Input
                  maxLength={20}
                  value={form.cmSlug}
                  onChange={(e) => set('cmSlug', e.target.value)}
                />
                <FieldError messages={fieldErrors('cmConfig.slug')} />
              </Field>
            </>
          )}
        </FormSection>
      )}

      {/* ----------------------------------------------------------------
          Sticky bottom action row (task 015-05 duplicate submit; Preview
          added 037-05; Save & close added 054-01). Preview is disabled for
          Co-op (no hosted page) and for CTO Email (no Offer page to render
          — 045-03), and while a save is in flight. 097-05 (#133) mirrors the
          create-mode Save & schedule in here too — the r15 case 2.2
          convention (HF-094): a bottom-up user must find every action in
          the row she is looking at.
      ----------------------------------------------------------------- */}
      <div
        className={clsx(
          'flex flex-wrap justify-end gap-2 sticky bottom-0 py-2 md:py-0 bg-paper md:bg-transparent',
          { invisible: !isVisibleHeader }
        )}
      >
        {/* Epic 095 D6: Preview reads bannerImageUrl, which a file choice
            clears while the upload is in flight — gated like the Save buttons. */}
        <Button
          type="button"
          variant="outline"
          className="min-h-11 md:min-h-0"
          disabled={!flags.hasOfferPage || submitting || imageUploading}
          onClick={handlePreview}
        >
          Preview
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 md:min-h-0"
          onClick={() => navigate('/campaigns')}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 md:min-h-0"
          disabled={submitting || imageUploading}
          onClick={() => void submit('close')}
        >
          Save &amp; close
        </Button>
        {canReopenFromForm && (
          <Button
            type="button"
            variant="outline"
            className="min-h-11 md:min-h-0"
            disabled={submitting || imageUploading}
            onClick={() => void submit('reopen')}
          >
            Save &amp; reopen
          </Button>
        )}
        {/* 097-05 (#133): the sticky-row twin of the top-row 069-03 D4 button —
            same gate (start date required), same handler; create mode only. */}
        {!isEdit && (
          <Button
            type="button"
            className="min-h-11 md:min-h-0"
            disabled={submitting || DATE_TO_MS(form.startDate) == null}
            onClick={() => void submit('stay', { scheduleOnCreate: true })}
          >
            Save &amp; schedule
          </Button>
        )}
        <Button
          type="submit"
          className="min-h-11 md:min-h-0"
          disabled={submitting || imageUploading}
        >
          {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
